import { Injectable } from '@angular/core';
import { syntaxTree, HighlightStyle, syntaxHighlighting } from "@codemirror/language"
import { EditorView, basicSetup } from "codemirror"
import { keymap } from "@codemirror/view"
import { Compartment, EditorState } from "@codemirror/state"
import { indentWithTab } from "@codemirror/commands"
import { autocompletion } from "@codemirror/autocomplete"
import { linter, diagnosticCount, forEachDiagnostic } from "@codemirror/lint"
import { snippetCompletion } from "@codemirror/autocomplete"
import { LRParser } from "@lezer/lr"
import { LRLanguage, LanguageSupport, foldNodeProp, foldInside, indentNodeProp } from "@codemirror/language"
import { styleTags, tags } from "@lezer/highlight"
import { buildParser } from '@lezer/generator';
import { Subject } from 'rxjs';
import { Engine } from 'json-rules-engine';
import { parseMixed } from "@lezer/common"
import { SharedService } from '../services/shared.service';
// import { SuggestionService } from './suggestion_rules';
import { TimeZoneService } from '../services/time-zone.service';

enum Info { Margin = 30, Width = 400 }
@Injectable({
  providedIn: 'root'
})

export class QueryEditorService {
  suggestion: any;
  mode: string;
  hostGrammar = `
    @detectDelim

    @tokens {
      space { @whitespace+ }
      ParenL[@name="openparam"] { '(' }
      ParenR[@name="closeparam"] { ')' } 
      SquareBracL { '[' }
      SquareBracR { ']' }
      Pipe { '|' }  
      Comma { ',' }
      Plus { "+" }
      Minus { "-" }
      Mul { "*" }
      Div { "/" }
      Units { $[0-9]$[0-9] | $[0-9] }
      NumericValue { "-"? $[0-9]+ ("." $[0-9]+)? }
      hour { $[0]$[0-9] | $[1]$[0-9] |  $[2]$[0-3]}
      minutes { $[0]$[0-9] | $[1-5]$[0-9] |  $[6]$[0]}
      day { $[0]$[0-9] | $[1]$[0-9] |  $[2]$[0-9] |  $[3]$[0-1]}
      month { $[0]$[0-9] | $[1]$[0-2]}
      year { ("199"$[0-9]| "20"$[0-4]$[0-9]) }
      Date { day "-" month "-" year space hour ":" minutes ":" minutes}
      content { (!["])* } 
      StringValue { '"' content '"' }
      ipdigit { ($[1-9]) | ($[1-9]$[0-9]) |  ( ($[1]$[0-9]$[0-9]) | ($[2]$[0-4]$[0-9]) | ($[2]$[5]$[0-5]) )}
      ip { ipdigit "." (ipdigit | $[0]) "." (ipdigit | $[0]) "." (ipdigit | $[0]) }
      IpAddress { "'" ip ("/" ($[0-9] | $[1-2]$[0-9] | $[3]$[0-2]))? "'"}
      partialip6 { "." (ipdigit | $[0]) }
      partialip8 { "." (ipdigit | $[0]) "." (ipdigit | $[0]) }
      partialip10 { "." (ipdigit | $[0]) "." (ipdigit | $[0]) "." (ipdigit | $[0]) }
      partialip11 { "." (ipdigit | $[0]) "." }
      partialip12 { "." (ipdigit | $[0]) "." (ipdigit | $[0]) "."}
      StartsWithIP { "'" ipdigit (partialip6 | partialip8 | partialip11 | partialip12 | ".")? "'"}
      EndsWithIP { "'" (partialip6 | partialip8 | partialip10)  ("/" ($[0-9] | $[1-2]$[0-9] | $[3]$[0-2]))?"'" }
      ContainsIP { "'" (partialip11 | partialip12) "'" }  
      identifier { $[A-Za-z_$] ($[A-Za-z0-9_$-])+ }  
      Dot { "." }    
      Semicolon { ";" }    
      quotedAlias { '"' (!["])* '"' }
      anyChar { ($[A-Za-z0-9_$="|%(),.'<>!+/*-])+ }
      EmbeddedTextToken { anyChar (space anyChar)*  }
  }

  @skip { space }

  @top HostScripts { 
    (ImportCommand+ LetCommand+ JoinCommand FilterCommand? FetchCommand SaveCommand?) |
    (ImportCommand+ Statement SaveCommand?) |
    (ImportCommand+ LetCommand+ Statement SaveCommand?)
  }

    ImportCommand { (import (Table | Tables) statsAlias?) }
    LetCommand { VariableAssignment ( Statement | VariableValue )  Semicolon}
    VariableAssignment { let Variable GenericOperator }
    VariableValue { StringValue | NumericValue | WildcardStringValue |  WildcardNumericValue | IpAddress | wildCardInIpValue }
    Statement { FromCommand WhenCommand Pipe EmbeddedTextToken } 
    FromCommand {from ( Alias ) }
    WhenCommand {Pipe when timeRange}
    timeRange { relativeTimes | literalTimes }
    relativeTimes { relativeTimePrefix Units relativeTimeUnit   }
    literalTimes { between ParenL Date Comma Date ParenR }
    JoinCommand {  Variable joinprefix? join Variable onCommand (joinprefix? join Variable onCommand)*  }
    onCommand { on ( onbasicexpression | onbasicclause | onbasicterm ) }
    onbasicexpression { (( onbasicterm | onbasicclause ) logicalOperator ( onbasicterm | onbasicclause )) ( logicalOperator ( onbasicterm | onbasicclause ) ) * }
    onbasicclause { ( logicalNegate? ParenL onbasicexpression ParenR ) | ( logicalNegate? ParenL onbasicclause ParenR ) }
    onbasicterm { onbasic | ( logicalNegate? ParenL onbasic ParenR ) }
    onbasic {  (joinFields) GenericOperator (joinFields) }
    joinFields { Variable Dot (stringFields | numericFields | ipFields | timestampFields | Alias ) }
    FilterCommand {Pipe filter  ( expression | clause | term ) }
    expression { (( term | clause ) logicalOperator ( term | clause )) ( logicalOperator ( term | clause ) ) * }
    clause { ( logicalNegate? ParenL expression ParenR ) | ( logicalNegate? ParenL clause ParenR ) }
    term { basic | ( logicalNegate? ParenL basic ParenR ) }
    basic { joinbasic | wildBasic | arithmaticbasic }
    joinbasic {joinFields (GenericOperator | NumericOperator) ( StringValue | NumericValue | IpAddress | joinFields | Variable ) }
    wildBasic { joinFields  ( inOperator | containsOperator | startswithOperator | endswithOperator )  (WildcardStringValue | wildCardInIpValue | WildcardNumericValue | Variable) }
    arithmaticbasic { ArithmaticExpression (GenericOperator | NumericOperator)  (NumericValue | ArithmaticExpression | joinFields)}
    FetchCommand { Pipe fetch ( fetchList | fetchall ) }
    SaveCommand { Pipe save as StringValue }
    fetchList { fetchItem | fetchItem (Comma fetchItem)* }
    fetchItem { (Variable Dot (stringFields | numericFields | timestampFields  | ipFields | Alias) statsAlias?) | Alias }
    Variable { identifier }
    WildcardStringValue { ParenL StringValue ( Comma StringValue)* ParenR }
    WildcardNumericValue { ParenL NumericValue ( Comma NumericValue)* ParenR }
    wildCardInIpValue[@name="WildCardIpValue"] { ParenL  IpAddress ( Comma IpAddress)* ParenR }
    statsAlias { as Alias }
    Tables { SquareBracL Table (Comma Table)* SquareBracR }
    Table { technology ("." vendor)? ("." model)? ("." class)? ("." type) ("." subtype)? }
    ArithmaticExpression { binaryExpression }
    binaryExpression { arithmaticTerm (arithmaticOperator arithmaticTerm)+ }
    arithmaticTerm { ParenL binaryExpression ParenR | arithmaticOperand }
    arithmaticOperand { NumericValue | (Variable Dot (numericFields | Alias)) }
    arithmaticOperator { Plus | Minus | Mul | Div }
    inOperator { (keywordInOperator<"IN">) }
    containsOperator { ( keywordContainsOperator<"CONTAINS" | "contains">) }
    startswithOperator { ( keywordStartswithOperator<"STARTSWITH" | "startswith">) }
    endswithOperator { ( keywordEndswithOperator<"ENDSWITH" | "endswith">) }


    save { (keywordSave<"SAVE">) }
    let { (keywordLet<"LET">) }
    when { (keywordWhen<"WHEN">) }
    join { (keywordJoin<"JOIN">) }
    filter { (keywordFilter<"FILTER">) }
    joinprefix { (keywordJoinPrefix<"LEFT" | "RIGHT" | "FULL">) }
    on { (keywordOn<"ON">) }
    between { (keywordBetween<"BETWEEN" | "Between" | "between">) }
    logicalOperator { (keywordLogical<"AND" | "OR" | "and" | "or">) }
    logicalNegate { (keywordNegate<"NOT" | "not">) }
    GenericOperator { "=" | "!="}
    NumericOperator { ">" | "<" | "<=" | ">="}
    as { ( keywordAs<"as" | "AS" >) }
    import { (keywordImport<"IMPORT">) }
    from { (keywordFrom<"FROM">) }
    fetch { ( keywordFetch<"FETCH">) }
    fetchall { ( keywordFetchall<"_all" | "_ALL">) }
    relativeTimeUnit { ( keywordRelativeTimeUnit<"hours" | "days" | "months">) }
    relativeTimePrefix { ( keywordRelativeTimePrefix<"Last"|"last">) }
    stringFields { (keywordString<"~~No_string_fieldsList">|keywordStringQuoted<"~~No_quoted_string_fieldsList">) }
    numericFields { (keywordNumeric<"~~No_numeric_fieldsList">|keywordNumericQuoted<"~~No_quoted_numeric_fieldsList">) }
    ipFields { ( keywordIpFields<"~~No_ip_string_fieldsList">|keywordIpFieldsQuoted<"~~No_quoted_ip_string_fieldsList">) }
    timestampFields { ( keywordTimestampFields<"~~No_timestamp_fieldsList">|keywordTimestampFieldsQuoted<"~~No_quoted_timestamp_string_fieldsList">) }
    technology { (keywordTechnology<"~~No_technology">) }
    vendor { (keywordVendor<"~~No_vendor">) }
    model { (keywordModel<"~~No_model">) }
    class { (keywordClass<"~~No_class">) }
    type { (keywordType<"~~No_type">) }
    subtype { (keywordSubtype<"~~No_subtype">) }
    keywordInOperator<term> { @specialize[@name = "InOperator"]<identifier, term> }
    keywordContainsOperator<term> { @specialize[@name = "ContainsOperator"]<identifier, term> }
    keywordStartswithOperator<term> { @specialize[@name = "StartswithOperator"]<identifier, term> }
    keywordEndswithOperator<term> { @specialize[@name = "EndswithOperator"]<identifier, term> }
    keywordSave<term> { @specialize[@name = "Save"]<identifier, term> }
    keywordRelativeTimeUnit<term> { @specialize[@name = "TimeUnit"]<identifier, term> }
    keywordRelativeTimePrefix<term> { @specialize[@name = "RelativeTimePrefix"]<identifier, term> }
    keywordLet<term> { @specialize[@name = "Let"]<identifier, term> }
    keywordWhen<term> { @specialize[@name = "When"]<identifier, term> }
    keywordJoin<term> { @specialize[@name = "Join"]<identifier, term> }
    keywordFilter<term> { @specialize[@name = "Filter"]<identifier, term> }
    keywordJoinPrefix<term> { @specialize[@name = "JoinPrefix"]<identifier, term> }
    keywordBetween<term> { @specialize[@name = "Between"]<identifier, term> }
    keywordOn<term> { @specialize[@name = "On"]<identifier, term> }
    keywordImport<term> { @specialize[@name = "Import"]<identifier, term> }
    keywordFrom<term> { @specialize[@name = "From"]<identifier, term> }
    keywordString<term> { @specialize[@name = "StringFields"]<identifier, term> }
    keywordStringQuoted<term> { @specialize[@name = "StringFields"]<quotedAlias, term> }
    keywordNumeric<term> { @specialize[@name = "NumericFields"]<identifier, term> }
    keywordNumericQuoted<term> { @specialize[@name = "NumericFields"]<quotedAlias, term> }
    keywordLogical<term> { @specialize[@name = "LogicalOperator"]<identifier, term> }
    keywordNegate<term> { @specialize[@name = "LogicalNegate"]<identifier, term> }
    keywordAs<term> { @specialize[@name = "As"]<identifier, term> }
    keywordFetch<term> { @specialize[@name = "Fetch"]<identifier, term> }
    keywordFetchall<term> { @specialize[@name = "Fetchall"]<identifier, term> }
    keywordIpFields<term> { @specialize[@name = "IpFields"]<identifier, term> }
    keywordIpFieldsQuoted<term> { @specialize[@name = "IpFields"]<quotedAlias, term> }
    keywordTimestampFields<term> { @specialize[@name = "TimestampFields"]<identifier, term> }
    keywordTimestampFieldsQuoted<term> { @specialize[@name = "TimestampFields"]<quotedAlias, term> }
    keywordTechnology<term> { @specialize<identifier, term> }
    keywordVendor<term> { @specialize<identifier, term> }
    keywordModel<term> { @specialize<identifier, term> }
    keywordClass<term> { @specialize<identifier, term> }
    keywordType<term> { @specialize<identifier, term> }
    keywordSubtype<term> { @specialize<identifier, term> }
    Alias { (quotedAlias   | identifier) }
  `;

  // customLang: LRLanguage;
  customLang: LRLanguage
  compiledNew: LRParser;
  constructor(private sharedService: SharedService,
    // private suggestion_service:SuggestionService,
    private timeZoneService: TimeZoneService
  ) {
  }

  enable_auto_suggestion = true
  editorValueSubject = new Subject<string>();
  editorValue$ = this.editorValueSubject.asObservable();
  lintChanges$ = new Subject<any>();

  search_rule_engine: any = null;
  alert_rule_engine: any = null;
  editorValue: string = "";

  myHighlightStyle = HighlightStyle.define([
    { tag: tags.keyword, color: "#2f17ff" },
    { tag: tags.string, color: "#f00" },
    { tag: tags.operator, color: "#000" },
    { tag: tags.variableName, color: "#ff3d00" },
    { tag: tags.number, color: "#1aa520" },
    { tag: tags.integer, color: "#f00" },
    { tag: tags.bracket, color: "#0027ff" },
    { tag: tags.bool, color: "#da00ff" },
    { tag: tags.logicOperator, color: "#000" },
    { tag: tags.list, color: "#CC8899" },
    { tag: tags.atom, color: "#0ab700" },
    { tag: tags.heading3, color: "#ff3d00" }
  ])
  parser_terms_table = {}
  myTheme = EditorView.theme({
    "&": {
      color: "#000",
      backgroundColor: "transparent"
    },
    "&.cm-editor": {
      height: "100%"
    },
    ".cm-content": {
      caretColor: "black",
      fontFamily: "Metropolis",
      fontSize: "14px",
      whiteSpace: "pre-wrap",
      minHeight: "10vh"
    },
    "&.cm-focused .cm-cursor": {
      borderLeftColor: "#000"
    },
    "&.cm-focused": {
      outline: "none"
    },
    ".cm-activeLine": {
      backgroundColor: "#ffffff00"
    },
    ".cm-activeLineGutter": {
      backgroundColor: "#ffffff00"
    },
    ".cm-gutters": {
      backgroundColor: "transparent",
      color: "#ddd",
      border: "none"
    },
    ".ͼb": {
      color: "#2f17ff",
    },
    ".ͼc": {
      color: "#f40",
    },
    ".cm-lintRange-error": {
      outline: "1px solid red",
      "border-radius": "5px",
      backgroundImage: "none"
    },
    ".cm-tooltip.cm-tooltip-autocomplete": {
      "& > ul": {
        fontFamily: "Metropolis",
        fontSize: "14px",
        whiteSpace: "nowrap",
        overflow: "hidden auto",
        maxWidth_fallback: "700px",
        maxWidth: "min(700px, 95vw)",
        minWidth: "250px",
        maxHeight: "10em",
        height: "100%",
        listStyle: "none",
        margin: 0,
        padding: 0,

        "& > li, & > completion-section": {
          padding: "1px 3px",
          lineHeight: 1.2
        },
        "& > li": {
          overflowX: "hidden",
          textOverflow: "ellipsis",
          cursor: "pointer"
        },
        "& > completion-section": {
          display: "list-item",
          borderBottom: "1px solid silver",
          paddingLeft: "0.5em",
          opacity: 0.7
        }
      }
    },

    ".cm-tooltip-autocomplete ul li[aria-selected]": {
      background: "#17c",
      color: "white",
    },

    ".cm-tooltip-autocomplete-disabled ul li[aria-selected]": {
      background: "#777",
    },

    ".cm-completionListIncompleteTop:before, .cm-completionListIncompleteBottom:after": {
      content: '"···"',
      opacity: 0.5,
      display: "block",
      textAlign: "center"
    },

    ".cm-tooltip.cm-completionInfo": {
      position: "absolute",
      padding: "3px 9px",
      width: "max-content",
      maxWidth: `${Info.Width}px`,
      boxSizing: "border-box"
    },

    ".cm-completionInfo.cm-completionInfo-left": { right: "100%" },
    ".cm-completionInfo.cm-completionInfo-right": { left: "100%" },
    ".cm-completionInfo.cm-completionInfo-left-narrow": { right: `${Info.Margin}px` },
    ".cm-completionInfo.cm-completionInfo-right-narrow": { left: `${Info.Margin}px` },

    // "&light .cm-snippetField": { backgroundColor: "#00000022" },
    // "&dark .cm-snippetField": { backgroundColor: "#ffffff22" },
    ".cm-snippetFieldPosition": {
      verticalAlign: "text-top",
      width: 0,
      height: "1.15em",
      display: "inline-block",
      margin: "0 -0.7px -.7em",
      borderLeft: "1.4px dotted #888"
    },

    ".cm-completionMatchedText": {
      textDecoration: "underline"
    },

    ".cm-completionDetail": {
      fontStyle: "italic",
      // textAlign: "right",
      color: "white",
      // width: "100%"
      marginLeft: "7em",
    },
    ".cm-completionIcon": {
      fontSize: "90%",
      width: ".8em",
      display: "inline-block",
      textAlign: "center",
      paddingRight: ".6em",
      opacity: "0.6",
      boxSizing: "content-box"
    },

    ".cm-completionIcon-params": {
      "&:after": { content: "'( )'" }
    },

    ".cm-completionIcon-logical_operator": {
      "&:after": { content: "'\u22A8'" }
    },

    ".cm-completionIcon-operator": {
      "&:after": { content: "'\u2261'" }
    },

    ".cm-completionIcon-arithmetic_operator": {
      "&:after": { content: "'\u00B1'" }
    },

    ".cm-completionIcon-command": {
      "&:after": { content: "'𝐶'" }
    },

    ".cm-completionIcon-keyword": {
      "&:after": { content: "'k'" } // Disable emoji rendering
      // "&:after": { content: "'🔑\u26BF'" } // Disable emoji rendering'ƒ'
    },


    ".cm-completionIcon-table": {
      "&:after": { content: "'T'", fontSize: "60%", verticalAlign: "middle" }
    },

    ".cm-completionIcon-string_fields": {
      "&:after": { content: "'abc'", fontSize: "60%", verticalAlign: "middle" }
    },

    ".cm-completionIcon-numeric_fields": {
      "&:after": { content: "'123'", fontSize: "60%", verticalAlign: "middle" }
    },

    ".cm-completionIcon-ip_fields": {
      "&:after": { content: "'ip'", fontSize: "60%", verticalAlign: "middle" }
    },

    ".cm-completionIcon-timestamp_fields": {
      "&:after": { content: "'ts'", fontSize: "60%", verticalAlign: "middle" }
    },

    ".cm-completionIcon-alias_name": {
      "&:after": { content: "'as'", fontSize: "60%", verticalAlign: "middle" }
    },

    ".cm-completionIcon-function": {
      "&:after": { content: "'ƒ'" }
    },

    ".cm-completionIcon-pipe": {
      "&:after": { content: "''" }
    },

    ".cm-completionIcon-string_value": {
      "&:after": { content: "'abc'", fontSize: "60%", verticalAlign: "middle" }
    },
    ".cm-completionIcon-numeric_value": {
      "&:after": { content: "'123'", fontSize: "60%", verticalAlign: "middle" }
    },
    ".cm-completionIcon-ip_value": {
      "&:after": { content: "'ip'", fontSize: "60%", verticalAlign: "middle" }
    },

    ".cm-completionIcon-string_list": {
      "&:after": { content: "'abc'", fontSize: "60%", verticalAlign: "middle" }
    },
    ".cm-completionIcon-numeric_list": {
      "&:after": { content: "'123'", fontSize: "60%", verticalAlign: "middle" }
    },
    ".cm-completionIcon-ip_list": {
      "&:after": { content: "'ip'", fontSize: "60%", verticalAlign: "middle" }
    },
    ".cm-completionIcon-class": {
      "&:after": { content: "'○'" }
    },
    ".cm-completionIcon-interface": {
      "&:after": { content: "'◌'" }
    },
    ".cm-completionIcon-variable": {
      "&:after": { content: "'𝑥'" }
    },
    ".cm-completionIcon-constant": {
      "&:after": { content: "'𝐶'" }
    },
    ".cm-completionIcon-type": {
      "&:after": { content: "'𝑡'" }
    },
    ".cm-completionIcon-enum": {
      "&:after": { content: "'∪'" }
    },
    ".cm-completionIcon-property": {
      "&:after": { content: "'□'" }
    },

    ".cm-completionIcon-namespace": {
      "&:after": { content: "'▢'" }
    },
    ".cm-diagnostic": {
      padding: "3px 6px 3px 8px",
      marginLeft: "-1px",
      display: "block",
      whiteSpace: "pre-wrap"
    },
    ".cm-diagnostic-error": { borderLeft: "5px solid #d11" },
    ".cm-diagnostic-warning": { borderLeft: "5px solid orange" },
    ".cm-diagnostic-info": { borderLeft: "5px solid #999" },
    ".cm-diagnostic-hint": { borderLeft: "5px solid #66d" },

    ".cm-diagnosticAction": {
      font: "inherit",
      border: "none",
      padding: "2px 4px",
      backgroundColor: "#444",
      color: "white",
      borderRadius: "3px",
      marginLeft: "8px",
      cursor: "pointer"
    },

    ".cm-diagnosticSource": {
      fontSize: "70%",
      opacity: .7
    },

    ".cm-tooltip-lint": {
      padding: 0,
      margin: 0,
      fontSize: "12px"
    },

    ".cm-lintPoint": {
      position: "relative",

      "&:after": {
        content: '""',
        position: "absolute",
        bottom: 0,
        left: "-2px",
        borderLeft: "3px solid transparent",
        borderRight: "3px solid transparent",
        borderBottom: "4px solid #d11"
      }
    },

    ".cm-lintPoint-warning": {
      "&:after": { borderBottomColor: "orange" }
    },
    ".cm-lintPoint-info": {
      "&:after": { borderBottomColor: "#999" }
    },
    ".cm-lintPoint-hint": {
      "&:after": { borderBottomColor: "#66d" }
    },

    ".cm-panel.cm-panel-lint": {
      position: "relative",
      "& ul": {
        maxHeight: "100px",
        overflowY: "auto",
        "& [aria-selected]": {
          backgroundColor: "#ddd",
          "& u": { textDecoration: "underline" }
        },
        "&:focus [aria-selected]": {
          background_fallback: "#bdf",
          backgroundColor: "Highlight",
          color_fallback: "white",
          color: "HighlightText"
        },
        "& u": { textDecoration: "none" },
        padding: 0,
        margin: 0
      },
      "& [name=close]": {
        position: "absolute",
        top: "0",
        right: "2px",
        background: "inherit",
        border: "none",
        font: "inherit",
        padding: 0,
        margin: 0
      }
    }
  }, { dark: false })

  Space = " \t\r\n"

  Ch = Object.freeze({
    Newline: 10,
    Space: 32,
    DoubleQuote: 34,
    Hash: 35,
    Dollar: 36,
    SingleQuote: 39,
    ParenL: 40, ParenR: 41,
    Star: 42,
    Plus: 43,
    Comma: 44,
    Dash: 45,
    Dot: 46,
    Slash: 47,
    Colon: 58,
    Semi: 59,
    Question: 63,
    At: 64,
    BracketL: 91, BracketR: 93,
    Backslash: 92,
    Underscore: 95,
    Backtick: 96,
    BraceL: 123, BraceR: 125,

    A: 65, a: 97,
    B: 66, b: 98,
    E: 69, e: 101,
    F: 70, f: 102,
    N: 78, n: 110,
    Q: 81, q: 113,
    X: 88, x: 120,
    Z: 90, z: 122,

    _0: 48, _1: 49, _9: 57,
  })

  processFields(platformFields: any[]) {
    const string_fields = [];
    const numeric_fields = [];
    const ip_fields = [];
    for (let i = 0; i < platformFields.length; i++) {
      if (platformFields[i].type == "text") {
        string_fields.push(platformFields[i].disp);
      }
      else if (platformFields[i].type == "inet") {
        ip_fields.push(platformFields[i].disp);
      }
      else {
        numeric_fields.push(platformFields[i].disp);
      }
    }

    sessionStorage.setItem("query_fields", JSON.stringify({ string_fields: string_fields, numeric_fields: numeric_fields, ip_fields: ip_fields }));
    return JSON.stringify({ string_fields: string_fields, numeric_fields: numeric_fields, ip_fields: ip_fields })
  }

  isAlpha(ch) {
    return ch >= this.Ch.A && ch <= this.Ch.Z || ch >= this.Ch.a && ch <= this.Ch.z || ch >= this.Ch._0 && ch <= this.Ch._9
  }

  readLiteral(input, endQuote, backslashEscapes) {
    for (let escaped = false; ;) {
      if (input.next < 0) {
        return
      }
      if (input.next == endQuote && !escaped) {
        input.advance();
        return
      }
      escaped = backslashEscapes && !escaped && input.next == this.Ch.Backslash
      input.advance()
    }
  }

  readWord(input, result) {
    for (; ;) {
      if (input.next != this.Ch.Underscore && !this.isAlpha(input.next)) break
      if (result != null) result += String.fromCharCode(input.next)
      input.advance()
    }
    return result
  }

  readValue(input, result) {
    for (; ;) {
      if (!this.isAlpha(input.next) && input.next == this.Ch.DoubleQuote) break
      if (result != null) result += String.fromCharCode(input.next)
      input.advance()
    }
    return result
  }
  isoperator(ch, str) {
    let char = "";
    for (let item of ch) {
      char = char + String.fromCharCode(item)
    }
    for (let kw of str) {
      if (kw == char) return true
    }
    return false
  }
  inString(ch, str) {
    for (let i = 0; i < str.length; i++) if (str.charCodeAt(i) == ch) return true
    return false
  }

  keywords(string_keywords, numeric_keywords) {
    let result = Object.create(null)
    for (let skw of string_keywords) if (skw) result[skw] = this.parser_terms_table["StringFields"]
    for (let nkw of numeric_keywords) if (nkw) result[nkw] = this.parser_terms_table["NumericFields"]
    return result
  }

  readIP(input, result) {
    for (; ;) {
      if (!(input.next >= this.Ch._0 && input.next <= this.Ch._9) && !this.isAlpha(input.next) && !(input.next == this.Ch.Colon) && !(input.next == this.Ch.Dot) && !(input.next == this.Ch.SingleQuote) && !(input.next == this.Ch.Slash)) break
      if (result != null) result += String.fromCharCode(input.next)
      input.advance()
    }
    return result
  }
  debugCursorLanguage(view) {
    const tree = syntaxTree(view.state)
    const node = tree.resolve(view.state.selection.main.head)
  }

  parserSubgrammar(platform: string, platfrom_plugins: any): any {
    return platfrom_plugins[platform]["customLang"].parser
  }


  buildHostGrammarFromTemplate(platform_plugins: any, partitions, mode?) {
    let platformsWithPartitions = Object.keys(platform_plugins).filter(value => value != "host")
    let newgrammar = this.hostGrammar

    let stringFields = new Set()
    let quotedStringFields = new Set()
    let numberFields = new Set()
    let quotedNumberFields = new Set()
    let ipFields = new Set()
    let quotedIpFields = new Set()
    let timeFields = new Set()
    let quotedTimeFields = new Set()
    let technology = new Set()
    let vendor = new Set()
    let model = new Set()
    let event_class = new Set()
    let type = new Set()
    let subtype = new Set()

    partitions.forEach(val => {
      technology.add(val.technology)
      vendor.add(val.vendor_product)
      val.model_device ? model.add(val.model_device) : null
      val.event_class ? event_class.add(val.event_class) : null
      type.add(val.partition_type)
      subtype.add(val.sub_type)
    })
    if (mode != 'alert') {
      platformsWithPartitions.forEach(element => {
        platform_plugins[element]["platform_fields"].forEach((item) => {
          if (item["display_type"] == "text") {
            if (item["value"].includes('"'))
              quotedStringFields.add(item["value"])
            else
              stringFields.add(item["value"])
          }
          else if (item["display_type"] == "number") {
            if (item["value"].includes('"'))
              quotedNumberFields.add(item["value"])
            else
              numberFields.add(item["value"])
          }
          else if (item["display_type"] == "timestamp") {
            if (item["value"].includes('"'))
              quotedTimeFields.add(item["value"])
            else
              timeFields.add(item["value"])
          }
          else if (item["display_type"] == "inet") {
            if (item["value"].includes('"'))
              quotedIpFields.add(item["value"])
            else
              ipFields.add(item["value"])
          }
        });
      });
    }
    function replcequotes(list) {
      let str = ""
      Array.from(list).forEach((element: string, index) => {
        str = str + element.replace(/"/g, '\\"')
        if (index < list.size - 1)
          str = str + '" | "'
      });
      return str
    }
   
    newgrammar = technology.size ? newgrammar.replace('"~~No_technology"', '"' + Array.from(technology).join('" | "') + '"') : newgrammar
    newgrammar = vendor.size ? newgrammar.replace('"~~No_vendor"', '"' + Array.from(vendor).join('" | "') + '"') : newgrammar
    newgrammar = model.size ? newgrammar.replace('"~~No_model"', '"' + Array.from(model).join('" | "') + '"') : newgrammar
    newgrammar = event_class.size ? newgrammar.replace('"~~No_class"', '"' + Array.from(event_class).join('" | "') + '"') : newgrammar
    newgrammar = type.size ? newgrammar.replace('"~~No_type"', '"' + Array.from(type).join('" | "') + '"') : newgrammar
    newgrammar = subtype.size ? newgrammar.replace('"~~No_subtype"', '"' + Array.from(subtype).join('" | "') + '"') : newgrammar

    newgrammar = stringFields.size ? newgrammar.replace('"~~No_string_fieldsList"', '"' + replcequotes(stringFields) + '"') : newgrammar
    newgrammar = numberFields.size ? newgrammar.replace('"~~No_numeric_fieldsList"', '"' + replcequotes(numberFields) + '"') : newgrammar
    newgrammar = ipFields.size ? newgrammar.replace('"~~No_ip_string_fieldsList"', '"' + replcequotes(ipFields) + '"') : newgrammar
    newgrammar = timeFields.size ? newgrammar.replace('"~~No_timestamp_fieldsList"', '"' + replcequotes(timeFields) + '"') : newgrammar

    newgrammar = quotedStringFields.size ? newgrammar.replace('"~~No_quoted_string_fieldsList"', '"' + replcequotes(quotedStringFields) + '"') : newgrammar
    newgrammar = quotedNumberFields.size ? newgrammar.replace('"~~No_quoted_numeric_fieldsList"', '"' + replcequotes(quotedNumberFields) + '"') : newgrammar
    newgrammar = quotedIpFields.size ? newgrammar.replace('"~~No_quoted_ip_string_fieldsList"', '"' + replcequotes(quotedIpFields) + '"') : newgrammar
    newgrammar = quotedTimeFields.size ? newgrammar.replace('"~~No_quoted_timestamp_string_fieldsList"', '"' + replcequotes(quotedTimeFields) + '"') : newgrammar
// console.log("<============newgrammar================>", newgrammar)
    return newgrammar
  }
  parser(platform_plugins: any, partitions, mode?) {
    let compiled = buildParser(this.buildHostGrammarFromTemplate(platform_plugins, partitions, mode));
    this.parser_terms_table = compiled["termTable"]
    let customLang = LRLanguage.define({ parser: compiled });
    return customLang.parser
  }

  qryLang2(platfrom_plugins: any, editormode) {
    let languages = []
// console.log(platfrom_plugins)
    Object.keys(platfrom_plugins).filter(value => (value != "host" && !Number.isInteger(Number(value)))).forEach(platform => {
      languages.push(new LanguageSupport(platfrom_plugins[platform]["customLang"], [
        syntaxHighlighting(this.myHighlightStyle),
        // linter(this.csiem_query_lint),
        // autocompletion({ activateOnCompletion: () => { return true } }),
        platfrom_plugins[platform]["customLang"].data.of({
          autocomplete: (context) => this.customCompletion(context, platfrom_plugins, "not_host", editormode)
        })
      ]))
    });
    return languages
  }

  qryLangAlertRule(platfrom_plugins: any, partitions?, alertId?, editormode?) {
    let languages = []
    Object.keys(platfrom_plugins).filter(value => value != "host" && !Number.isInteger(Number(value))).forEach(platform => {
      languages.push(new LanguageSupport(platfrom_plugins[platform]["customLang"], [
        syntaxHighlighting(this.myHighlightStyle),
        linter(this.csiem_query_lint),
        // autocompletion({ activateOnCompletion: () => { return true } }),
        platfrom_plugins[platform]["customLang"].data.of({
          // autocomplete: this.customCompletion.bind(this)
          autocomplete: (context) => this.customCompletionAlert(context, platfrom_plugins, "", editormode)
        })
      ]))
    });
    return languages
  }

  createSuggestionRuleEngine(mode) {
    if (mode != "alert") {
      if (!this.search_rule_engine) {
        this.search_rule_engine = new Engine()
        Object.keys({ ...this.sharedService.suggestion_rules["events"], ...this.sharedService.suggestion_rules["host"] }).forEach(element => {
          this.search_rule_engine.addRule({ ...this.sharedService.suggestion_rules["events"], ...this.sharedService.suggestion_rules["host"] }[element])
        });
      }
    } else {
      if (!this.alert_rule_engine) {
        this.alert_rule_engine = new Engine()
        Object.keys(this.sharedService.suggestion_rules["alerts"]).forEach(element => {
          this.alert_rule_engine.addRule(this.sharedService.suggestion_rules["alerts"][element])
        });
      }
    }

  }

  parseWithMetadata(platform_plugins, partitions, mode?) {
    this.createSuggestionRuleEngine(mode)
    return this.parser(platform_plugins, partitions, mode).configure({
      props: [
        styleTags({
          Relativetimes: tags.keyword,
          StringFields: tags.keyword,
          NumericFields: tags.keyword,
          IpFields: tags.keyword,
          GenericOperator: tags.operator,
          NumericOperator: tags.operator,
          LogicalOperator: tags.logicOperator,
          LogicalNegate: tags.logicOperator,
          InOperator: tags.variableName,
          ContainsOperator: tags.variableName,
          StartswithOperator: tags.variableName,
          EndswithOperator: tags.variableName,
          StringValue: tags.string,
          Date: tags.keyword,
          Alias: tags.string,
          Platform: tags.number,
          NumericValue: tags.number,
          IpAddress: tags.integer,
          ContainsIP: tags.integer,
          StartsWithIP: tags.integer,
          EndsWithIP: tags.integer,
          CommonIP: tags.integer,
          openparam: tags.bracket,
          closeparam: tags.bracket,
          From: tags.bool,
          When: tags.bool,
          Let: tags.bool,
          Join: tags.bool,
          On: tags.bool,
          Search: tags.bool,
          Calculate: tags.bool,
          By: tags.bool,
          Fetch: tags.bool,
          Where: tags.bool,
          Sort: tags.bool,
          Limit: tags.bool,
          Filter: tags.bool,
          Import: tags.bool,
          Table: tags.number,
          Variable: tags.list,
          Distinct: tags.operator,
          FetchAll: tags.operator,
          SortOperator: tags.operator,
          StringListValue: tags.list,
          NumericListValue: tags.list,
          IPListValue: tags.list,
          HashTags: tags.heading3,
          StringFunctionType1Name: tags.atom,
          StringFunctionType2Name: tags.atom,
          StringFunctionType3Name: tags.atom,
          StringFunctionType4Name: tags.atom,
          StringFunctionType5Name: tags.atom,
          StringFunctionType6Name: tags.atom,
          NumberFunctionType1Name: tags.atom,
          NumberFunctionType2Name: tags.atom,
          NumberFunctionType3Name: tags.atom,
          TimestampFunctionType1Name: tags.atom,
          TimestampFunctionType2Name: tags.atom,
          ListFunctionType1Name: tags.atom,
          TimestampFields: tags.keyword,
          TimeFormat: tags.string,

        }),
        indentNodeProp.add({
          Application: context => context.column(context.node.from) + context.unit
        }),
        foldNodeProp.add({
          Application: foldInside
        })
      ],
      wrap: parseMixed((node, input) => {
        if (node.name == "EmbeddedTextToken") {
          let FromCommand
          FromCommand = node.node?.prevSibling?.prevSibling?.prevSibling?.node
          let platfromToken = FromCommand?.getChild("Table")?.node
          let platform = ""
          if (platfromToken)
            platform = this.sharedService.partitionsMapObj[input.read(platfromToken?.from, platfromToken?.to)]
          else {
            let platfromAlias = input.read(FromCommand?.getChild("Alias")?.from, FromCommand?.getChild("Alias")?.to)
            let HostScript = node.node?.parent?.node.parent?.node?.parent ? node.node?.parent?.node.parent?.node?.parent : node.node?.parent?.node.prevSibling?.parent?.node
            HostScript?.getChildren("ImportCommand")?.forEach(command => {
              if (platfromAlias == input.read(command?.getChild("Alias")?.from, command?.getChild("Alias")?.to)) {
                let alaisToken = command.getChild("Alias")?.node
                let importAlias = input.read(command?.getChild("Alias")?.from, command?.getChild("Alias")?.to)
                let platformToken = command.getChild("Table")?.node
                let tablesToken = command.getChild("Tables")?.getChildren("Table")
                let tables = []
                tablesToken?.forEach(element => {
                  tables.push(input.read(element.from, element.to))
                });
                let id

                if (tables.length) {
                  partitions.forEach(part => {
                    if (part.partition_name_disp == tables[0]) {
                      id = part.platform_id
                    }
                  })

                }


                let alias = input.read(alaisToken?.from, alaisToken?.to)

                let pf = tables.length > 1 ? id : (input.read(platformToken?.from, platformToken?.to) ? this.sharedService.partitionsMapObj[input.read(platformToken?.from, platformToken?.to)] : this.sharedService.partitionsMapObj[tables[0]])
                if (alias == platfromAlias) {
                  platform = pf
                }
              }
            });

          }
          if (platform && platform in platform_plugins)
            return { parser: platform_plugins[platform]["customLang"].parser }
          else if (platform && platform in this.sharedService.platform_plugins) {
            return { parser: this.sharedService.platform_plugins[platform]["customLang"].parser }
          }
          else
            return null
        }
        return null
      })
    })
  }

  csiem_query_Language(platfrom_plugins: string, partitions, mode?) {
    return LRLanguage.define({
      parser: this.parseWithMetadata(platfrom_plugins, partitions, mode),

    })
  }



  csiem_host_lint(view: any, mode = "search") {
    let diagnostics: any = []
    let field_alias = {}
    let variable_fields = {}
    let variables = {}
    let tableAlias = {}
    let tables = {}
    let datatypes = {
      "StringFields": ["WildcardStringValue", "StringValue"],
      "NumericFields": ["NumericValue", "WildcardNumericValue"],
      "IpFields": ["IpAddress", "WildCardIpValue"],
    }
    let datatypesOperators = {
      "StringValue": ["GenericOperator"],
      "WildcardStringValue": ["StartswithOperator", "EndswithOperator", "InOperator"],
      "NumericValue": ["GenericOperator", "NumericOperator"],
      "WildcardNumericValue": ["InOperator"],
      "IpAddress": ["GenericOperator"],
      "WildCardIpValue": ["StartswithOperator", "EndswithOperator", "InOperator"],
    }

    try {
      let ImportCommand = syntaxTree(view.state).cursor().node.getChildren("ImportCommand")
      let LetCommand = syntaxTree(view.state).cursor().node.getChildren("LetCommand")
      let Statement = syntaxTree(view.state).cursor().node.getChildren("Statement")
      let JoinCommand = syntaxTree(view.state).cursor().node.getChildren("JoinCommand")
      let FilterCommand = syntaxTree(view.state).cursor().node.getChildren("FilterCommand")
      let FetchCommand = syntaxTree(view.state).cursor().node.getChildren("FetchCommand")
      const containsAll = (mainArray, targetArray) => {
        return targetArray.every(element => mainArray.includes(element));
      };
      ImportCommand?.forEach((command) => {
        let alaisToken = command.getChild("Alias")?.node
        let platformToken = command.getChild("Table")?.node
        let TablesToken = command.getChild("Tables")?.node
        let multipleTableSelection = TablesToken?.getChildren("Table")
        let alias = view.state.sliceDoc(alaisToken?.from, alaisToken?.to)
        let platform = ""
        let multitable: any = []
        let groups = {}
        if (multipleTableSelection) {
          multipleTableSelection?.forEach(element => {
            let tablename = view.state.sliceDoc(element?.from, element?.to)
            // duplicate sources in one combination
            if (multitable.includes(tablename)) {
              diagnostics.push({
                from: element.from,
                to: element.to,
                severity: "error",
                message: `Duplicate source '${tablename}'`,
              })
            }
            // user defined platfroms cannot come in rules
            if (mode == "rule" && this.sharedService.partitions["rows"].find(element => element.partition_name_disp == tablename && element.platform_id == 0)) {
              diagnostics.push({
                from: element.from,
                to: element.to,
                severity: "error",
                message: `source '${tablename}' not allowed`
              })
            }
            multitable.push(tablename)
            multitable?.forEach(partition => {
              let result = this.sharedService.partitions["rows"].filter(item => item["partition_name_disp"] == partition)
              result?.forEach(item => {
                item["platform_id"] in groups ? groups[item["platform_id"]].push(element) : groups[item["platform_id"]] = [element]
              });
            });
          });
          // duplicate combined sources
          Object.keys(tableAlias)?.forEach(key => {
            if (containsAll(multitable, tableAlias[key])) {
              diagnostics.push({
                from: TablesToken?.from,
                to: TablesToken?.to,
                severity: "error",
                message: `Duplicate sources '${multitable.join(',')}'`,
              })
            }
          });
          // sources from different platforms cannot be combined
          if (Object.keys(groups).length > 1) {
            diagnostics.push({
              from: TablesToken.from,
              to: TablesToken.to,
              severity: "error",
              message: `Sources from different platforms cannot be combined.`,
            })
          }
          // user defined platforms cannot be combined
          Object.keys(groups).forEach((platform_id) => {
//console.log(platform_id, typeof (platform_id))
            if (platform_id == "0" && groups[platform_id].length > 1) {
              diagnostics.push({
                from: groups[platform_id][0].from,
                to: groups[platform_id][0].to,
                severity: "error",
                message: `Sources from different platforms cannot be combined.`
              })
            }
          });
        }
        else {
          platform = view.state.sliceDoc(platformToken?.from, platformToken?.to)
          if ((platform) in tables) {
            diagnostics.push({
              from: platformToken.from,
              to: platformToken.to,
              severity: "error",
              message: `Duplicate source '${platform}'`,
            })
          }

          if (mode == "rule" && this.sharedService.partitions["rows"].find(element => element.partition_name_disp == platform && element.platform_id == 0)) {
            diagnostics.push({
              from: platformToken.from,
              to: platformToken.to,
              severity: "error",
              message: `source '${platform}' not allowed`
            })
          }
        }
        if (alaisToken && alias in tableAlias) {
          diagnostics.push({
            from: alaisToken.from,
            to: alaisToken.to,
            severity: "error",
            message: `Duplicate source alias '${alias}'`,
          })
        }
        tables[platform] = { "platform": platform, "alias": alias }
        tableAlias[alias] = multipleTableSelection ? multitable : [platform]
      })
      LetCommand?.forEach((command, index) => {
        let element = command.getChild("VariableAssignment")?.getChild("Variable")?.node
        let variable = element ? view.state.sliceDoc(element.from, element.to) : null
        field_alias[variable] = []
        variable_fields[variable] = []
        let StatementToken = command.getChild("Statement")
        let variableType = StatementToken ? StatementToken?.node.name : command.getChild("VariableValue")?.firstChild?.node.name
        let tableAliasToken = StatementToken?.getChild("FromCommand")?.getChild("Alias")?.node
// console.log("From command -> ",StatementToken)
        let Alias = view.state.sliceDoc(tableAliasToken?.from, tableAliasToken?.to)
        let WhenCommandTokens = StatementToken?.getChild("WhenCommand")
        let FromCommandTokens = StatementToken?.getChild("FromCommand")
        let StatementFetchCommand = StatementToken?.getChild("Scripts")?.getChild("FetchCommand")
        let StatementSortCommand = StatementToken?.getChild("Scripts")?.getChild("SortCommand")

        let StatementCalculateCommand = StatementToken?.getChild("Scripts")?.getChild("CalculateCommand")
        let StatementSearchCommand = StatementToken?.getChild("Scripts")?.getChild("SearchCommand")
        let StatementByCommand = StatementToken?.getChild("Scripts")?.getChild("ByCommand")
        let usedVariablesTokens = StatementSearchCommand?.getChildren("Variable")
        let usedAliasTokens = StatementCalculateCommand?.getChildren("Alias")
        let AliasTokensUsedInWhere = StatementToken?.getChild("Scripts")?.getChild("WhereCommand")?.getChildren("Alias")
        let AliasTokensUsedInFetch = StatementFetchCommand?.getChildren("Alias")
        let suggestions = null
        let commands = []




        // variables used in SearchCommand
        usedVariablesTokens?.forEach(element => {
          let variableUsed = view.state.sliceDoc(element.from, element.to)
          let respectiveOperatorType = element.prevSibling
          let respectiveFieldType = element.prevSibling?.prevSibling
          let respectiveOperator = view.state.sliceDoc(respectiveOperatorType.from, respectiveOperatorType.to)
          let respectiveField = view.state.sliceDoc(respectiveFieldType.from, respectiveFieldType.to)
          if (!datatypes[respectiveFieldType.node.name]?.includes(variables[variableUsed])) {
            diagnostics.push({
              from: element?.from,
              to: element?.to,
              severity: "error",
              message: `Invalid value '${variableUsed}' for field '${respectiveField}': incompatible type`,
            })
          }
          if (!datatypesOperators[variables[variableUsed]]?.includes(respectiveOperatorType.node.name)) {
            diagnostics.push({
              from: respectiveOperatorType?.from,
              to: respectiveOperatorType?.to,
              severity: "error",
              message: `Invalid operator '${respectiveOperator}' for field '${respectiveField}': incompatible type`,
            })
          }
        })
        //Alias used in Calculate command
        usedAliasTokens?.forEach(element => {
          let alias = view.state.sliceDoc(element.from, element.to)
          if (field_alias[variable].includes(alias)) {
            diagnostics.push({
              from: element.from,
              to: element.to,
              severity: "error",
              message: `Duplicate alias ${alias}`,
            })
          }
          field_alias[variable].push(alias)
        });
        //Alias used in Where command
        AliasTokensUsedInWhere?.forEach(element => {
          let alias = view.state.sliceDoc(element.from, element.to)
          if (!field_alias[variable].includes(alias)) {
            diagnostics.push({
              from: element.from,
              to: element.to,
              severity: "error",
              message: `Undefined alias ${alias}`,
            })
          }
          field_alias[variable].push(alias)
        });
        //Alias used in Fetch command
        AliasTokensUsedInFetch?.forEach(element => {
          let alias = view.state.sliceDoc(element.from, element.to)
          if (!field_alias[variable].includes(alias)) {
            diagnostics.push({
              from: element.from,
              to: element.to,
              severity: "error",
              message: `Undefined field ${alias}`,
            })
          }
          field_alias[variable].push(alias)
        });
        // duplicate variables
        if (variable in variables) {
          diagnostics.push({
            from: element.from,
            to: element.to,
            severity: "error",
            message: `cannot redeclare variable '${variable}'`,
          })
        }
        variables[variable] = variableType
// console.log("Alias table 1 ->",tableAlias)
// console.log("Alias 1 ->",Alias)
// console.log("Table alias token 1 -> ",tableAliasToken)
        // undefined variables
        if (!(Alias in tableAlias) && tableAliasToken) {
          diagnostics.push({
            from: tableAliasToken?.from,
            to: tableAliasToken?.to,
            severity: "error",
            message: `Undefined table '${Alias}'`,
          })
        }
        //fields used in Fetch
        let applicableFields = { "StringFields": {}, "NumericFields": {}, "IpFields": {}, "TimestampFields": {}, "Alias": {} }

        //multi partition is used on a variable
        if ((Alias in tableAlias) && tableAlias[Alias].length > 1) {
          // to store distinct and common fields from all the partitions combined by grouping the  token type
          tableAlias[Alias]?.forEach(partition => {
            // populating common and distinct fields across partitons into  applicableFields
            if (partition in this.sharedService.platform_plugins) {
              this.sharedService.platform_plugins[partition]["platform_fields"]?.forEach(fieldobj => {
                if (fieldobj["display_type"] == "text") {
                  applicableFields["StringFields"][fieldobj["value"]] = fieldobj["value"] in applicableFields["StringFields"] ? applicableFields["StringFields"][fieldobj["value"]] + 1 : 1
                } else if (fieldobj["display_type"] == "number") {
                  applicableFields["NumericFields"][fieldobj["value"]] = fieldobj["value"] in applicableFields["NumericFields"] ? applicableFields["NumericFields"][fieldobj["value"]] + 1 : 1
                } else if (fieldobj["display_type"] == "timestamp") {
                  applicableFields["TimestampFields"][fieldobj["value"]] = fieldobj["value"] in applicableFields["TimestampFields"] ? applicableFields["TimestampFields"][fieldobj["value"]] + 1 : 1
                } else if (fieldobj["display_type"] == "inet") {
                  applicableFields["IpFields"][fieldobj["value"]] = fieldobj["value"] in applicableFields["IpFields"] ? applicableFields["IpFields"][fieldobj["value"]] + 1 : 1
                }
              });
            }
          });
          let fieldsInCommands = {
            "SearchCommand": { "StringFields": {}, "NumericFields": {}, "IpFields": {}, "TimestampFields": {}, "Alias": {} },
            "CalculateCommand": { "StringFields": {}, "NumericFields": {}, "IpFields": {}, "TimestampFields": {}, "Alias": {} },
            "ByCommand": { "StringFields": {}, "NumericFields": {}, "IpFields": {}, "TimestampFields": {}, "Alias": {} },
            "WhereCommand": { "StringFields": {}, "NumericFields": {}, "IpFields": {}, "TimestampFields": {}, "Alias": {} },
          }
          // iterate through commands
          Object.keys(fieldsInCommands).forEach(command => {
            // iterate through types of fields
            Object.keys(fieldsInCommands[command]).forEach(fieldtype => {
              let CommandFields = StatementToken?.getChild("Scripts")?.getChild(command)?.getChildren(fieldtype)
              CommandFields?.forEach(element => {
                let field = view.state.sliceDoc(element.from, element.to)
                if (element.name in applicableFields) {
                  if (applicableFields[element.name][field] < 2) {
                    diagnostics.push({
                      from: element?.from,
                      to: element?.to,
                      severity: "error",
                      message: `Field '${field}' not applicable for table '${Alias}'`,
                    })
                  }
                }
              });

            });


          });
          //check only applicable fields is present in fetch command
          if (!StatementFetchCommand?.getChild("Fetchall")) {
            let calculateCommandFields = []
            // any fields other than alias from calculate is wrong
            if (StatementFetchCommand?.prevSibling?.name == 'CalculateCommand') {
              let suggestions = this.find_alias_name([StatementFetchCommand?.prevSibling], view, tableAlias[Alias], mode)
              suggestions["alias_names"].forEach(element => {
                calculateCommandFields.push(element["name"])
              });
              //   let referenceFields = suggestions["alias_names"].concat(suggestions["field_names"])


            }
            else if (StatementFetchCommand?.prevSibling?.name == 'ByCommand') {
              let suggestions = this.find_alias_name([StatementFetchCommand?.prevSibling, StatementFetchCommand?.prevSibling?.prevSibling], view, tableAlias[Alias], mode)
              suggestions["alias_names"].forEach(element => {
                calculateCommandFields.push(element["name"])
              });
              suggestions["field_names"].forEach(element => {
                calculateCommandFields.push(element["name"])
              });
              //   let referenceFields = suggestions["alias_names"].concat(suggestions["field_names"])


            }
            else if (StatementFetchCommand?.prevSibling?.name == 'SearchCommand') {
              let fieldsAvailableForFetch = new Set()
              tableAlias[Alias].forEach(partition => {
                this.sharedService.platform_plugins[partition]["platform_fields"].forEach(flds => {
                  fieldsAvailableForFetch.add(flds["value"])
                });
              });
              calculateCommandFields = Array.from(fieldsAvailableForFetch)

            }
            Object.keys(fieldsInCommands["SearchCommand"]).forEach(fieldtype => {
              let CommandFields = StatementToken?.getChild("Scripts")?.getChild("FetchCommand")?.getChildren(fieldtype)
              CommandFields?.forEach(element => {
                let field = view.state.sliceDoc(element.from, element.to)
                if (!(calculateCommandFields.includes(field))) {
                  diagnostics.push({
                    from: element?.from,
                    to: element?.to,
                    severity: "error",
                    message: `Field '${field}' not applicable for FETCH.`,
                  })
                }
              });
            });
          }
          //check only applicable fields is present in sort command
          if (StatementSortCommand) {
            let calculateCommandFields = []

            if (StatementFetchCommand?.prevSibling?.name == 'CalculateCommand') {
              let suggestions = this.find_alias_name([StatementFetchCommand?.prevSibling], view, tableAlias[Alias], mode)
              suggestions["alias_names"].forEach(element => {
                calculateCommandFields.push(element["name"])
              });
              //   let referenceFields = suggestions["alias_names"].concat(suggestions["field_names"])


            }
            else if (StatementFetchCommand?.prevSibling?.name == 'ByCommand') {
              let suggestions = this.find_alias_name([StatementFetchCommand?.prevSibling, StatementFetchCommand?.prevSibling?.prevSibling], view, tableAlias[Alias], mode)
              suggestions["alias_names"].forEach(element => {
                calculateCommandFields.push(element["name"])
              });
              suggestions["field_names"].forEach(element => {
                calculateCommandFields.push(element["name"])
              });
              //   let referenceFields = suggestions["alias_names"].concat(suggestions["field_names"])


            }
            else if (StatementFetchCommand?.prevSibling?.name == 'SearchCommand') {
              let fieldsAvailableForFetch = new Set()
              tableAlias[Alias].forEach(partition => {
                this.sharedService.platform_plugins[partition]["platform_fields"].forEach(flds => {
                  fieldsAvailableForFetch.add(flds["value"])
                });
              });
              calculateCommandFields = Array.from(fieldsAvailableForFetch)

            }
            Object.keys(fieldsInCommands["SearchCommand"]).forEach(fieldtype => {
              let CommandFields = StatementToken?.getChild("Scripts")?.getChild("SortCommand")?.getChildren(fieldtype)
              CommandFields?.forEach(element => {
                let field = view.state.sliceDoc(element.from, element.to)
                if (!(calculateCommandFields.includes(field))) {
                  diagnostics.push({
                    from: element?.from,
                    to: element?.to,
                    severity: "error",
                    message: `Field '${field}' not applicable for SORT.`,
                  })
                }
              });
            });
          }
          // let SearchCommandAliasFields = StatementToken?.getChild("Scripts")?.getChild("SearchCommand")?.getChildren("Alias")

        }
        let fieldsInCommands = {
          "SearchCommand": { "StringFields": {}, "NumericFields": {}, "IpFields": {}, "TimestampFields": {}, "Alias": {} },
          "CalculateCommand": { "StringFields": {}, "NumericFields": {}, "IpFields": {}, "TimestampFields": {}, "Alias": {} },
          "ByCommand": { "StringFields": {}, "NumericFields": {}, "IpFields": {}, "TimestampFields": {}, "Alias": {} },
          "WhereCommand": { "StringFields": {}, "NumericFields": {}, "IpFields": {}, "TimestampFields": {}, "Alias": {} },
        }
        // iterate through commands
        Object.keys(fieldsInCommands).forEach(command => {
          // iterate through types of fields
          Object.keys(fieldsInCommands[command]).forEach(fieldtype => {
            let CommandFields = StatementToken?.getChild("Scripts")?.getChild(command)?.getChildren(fieldtype)
            CommandFields?.forEach(element => {
              let field = view.state.sliceDoc(element.from, element.to)
              if (element.name in applicableFields) {
                if (applicableFields[element.name][field] < 2) {
                  diagnostics.push({
                    from: element?.from,
                    to: element?.to,
                    severity: "error",
                    message: `Field '${field}' not applicable for table '${Alias}'`,
                  })
                }
              }
            });

          });


        });
        //check only applicable fields is present in fetch command
        if (!StatementFetchCommand?.getChild("Fetchall")) {
          let calculateCommandFields = []
          // any fields other than alias from calculate is wrong
          if (StatementFetchCommand?.prevSibling?.name == 'CalculateCommand') {
            let suggestions = this.find_alias_name([StatementFetchCommand?.prevSibling], view, tableAlias[Alias], mode)
            suggestions["alias_names"].forEach(element => {
              calculateCommandFields.push(element["name"])
            });
            //   let referenceFields = suggestions["alias_names"].concat(suggestions["field_names"])


          }
          else if (StatementFetchCommand?.prevSibling?.name == 'ByCommand') {
            let suggestions = this.find_alias_name([StatementFetchCommand?.prevSibling, StatementFetchCommand?.prevSibling?.prevSibling], view, tableAlias[Alias], mode)
            suggestions["alias_names"].forEach(element => {
              calculateCommandFields.push(element["name"])
            });
            suggestions["field_names"].forEach(element => {
              calculateCommandFields.push(element["name"])
            });
            //   let referenceFields = suggestions["alias_names"].concat(suggestions["field_names"])


          }
          else if (StatementFetchCommand?.prevSibling?.name == 'SearchCommand') {
            let fieldsAvailableForFetch = new Set()
            tableAlias[Alias].forEach(partition => {
              this.sharedService.platform_plugins[partition]["platform_fields"].forEach(flds => {
                fieldsAvailableForFetch.add(flds["value"])
              });
            });
            calculateCommandFields = Array.from(fieldsAvailableForFetch)

          }
          Object.keys(fieldsInCommands["SearchCommand"]).forEach(fieldtype => {
            let CommandFields = StatementToken?.getChild("Scripts")?.getChild("FetchCommand")?.getChildren(fieldtype)
            CommandFields?.forEach(element => {
              let field = view.state.sliceDoc(element.from, element.to)
              if (!(calculateCommandFields.includes(field))) {
                diagnostics.push({
                  from: element?.from,
                  to: element?.to,
                  severity: "error",
                  message: `Field '${field}' not applicable for FETCH.`,
                })
              }
            });
          });
        }
        //check only applicable fields is present in sort command
        if (StatementSortCommand) {
          let calculateCommandFields = []

          if (StatementFetchCommand?.prevSibling?.name == 'CalculateCommand') {
            let suggestions = this.find_alias_name([StatementFetchCommand?.prevSibling], view, tableAlias[Alias], mode)
            suggestions["alias_names"].forEach(element => {
              calculateCommandFields.push(element["name"])
            });
            //   let referenceFields = suggestions["alias_names"].concat(suggestions["field_names"])


          }
          else if (StatementFetchCommand?.prevSibling?.name == 'ByCommand') {
            let suggestions = this.find_alias_name([StatementFetchCommand?.prevSibling, StatementFetchCommand?.prevSibling?.prevSibling], view, tableAlias[Alias], mode)
            suggestions["alias_names"].forEach(element => {
              calculateCommandFields.push(element["name"])
            });
            suggestions["field_names"].forEach(element => {
              calculateCommandFields.push(element["name"])
            });
            //   let referenceFields = suggestions["alias_names"].concat(suggestions["field_names"])


          }
          else if (StatementFetchCommand?.prevSibling?.name == 'SearchCommand') {
            let fieldsAvailableForFetch = new Set()
            tableAlias[Alias].forEach(partition => {
              this.sharedService.platform_plugins[partition]["platform_fields"].forEach(flds => {
                fieldsAvailableForFetch.add(flds["value"])
              });
            });
            calculateCommandFields = Array.from(fieldsAvailableForFetch)

          }
          Object.keys(fieldsInCommands["SearchCommand"]).forEach(fieldtype => {
            let CommandFields = StatementToken?.getChild("Scripts")?.getChild("SortCommand")?.getChildren(fieldtype)
            CommandFields?.forEach(element => {
              let field = view.state.sliceDoc(element.from, element.to)
              if (!(calculateCommandFields.includes(field))) {
                diagnostics.push({
                  from: element?.from,
                  to: element?.to,
                  severity: "error",
                  message: `Field '${field}' not applicable for SORT.`,
                })
              }
            });
          });
        }

        if (StatementFetchCommand?.getChild("Fetchall")) {

          if (StatementFetchCommand?.prevSibling?.name == "CalculateCommand") {
            suggestions = this.find_alias_name([StatementFetchCommand?.prevSibling], view, tableAlias[Alias], mode)
          }
          else if (StatementFetchCommand?.prevSibling?.name == "ByCommand") {
            suggestions = this.find_alias_name([StatementFetchCommand?.prevSibling, StatementFetchCommand?.prevSibling?.prevSibling], view, tableAlias[Alias], mode)
          }
          else if (StatementFetchCommand?.prevSibling?.name == "WhereCommand") {
            if (StatementFetchCommand?.prevSibling?.prevSibling?.name == "CalculateCommand") {
              suggestions = this.find_alias_name([StatementFetchCommand?.prevSibling?.prevSibling], view, tableAlias[Alias], mode)
            }
            else {
              suggestions = this.find_alias_name([StatementFetchCommand?.prevSibling?.prevSibling, StatementFetchCommand?.prevSibling?.prevSibling?.prevSibling], view, tableAlias[Alias], mode)
            }
          }
          else {
            suggestions = this.find_alias_name([StatementFetchCommand], view, tableAlias[Alias], mode)
          }

        }
        else {
          suggestions = this.find_alias_name([StatementFetchCommand], view, tableAlias[Alias], mode)
        }
//console.log(suggestions)

        suggestions["alias_names"]?.forEach(names => {
          if (field_alias[variable].includes(names.name))
            diagnostics.push({
              from: names?.pos,
              to: names?.length,
              severity: "error",
              message: `Duplicate field '${names.name}'`,
            })
          else {
            field_alias[variable].push(names.name)
          }

          if (!variable_fields[variable].includes(names.name)) {
            variable_fields[variable].push(names.name)
          }
        });


        suggestions["field_names"]?.forEach(names => {
          if (variable_fields[variable].includes(names.name)) {
            diagnostics.push({
              from: names?.pos,
              to: names?.length,
              severity: "error",
              message: `Duplicate field '${names.name}'`,
            })
          } else {
            variable_fields[variable].push(names.name)
          }
        })
      });

      Statement?.forEach(command => {
        let field_alias = []
        let StatementToken = command
        let variableType = StatementToken ? StatementToken?.node.name : command.getChild("VariableValue")?.firstChild?.node.name
        let tableAliasToken = StatementToken?.getChild("FromCommand")?.getChild("Alias")?.node
        let Alias = view.state.sliceDoc(tableAliasToken?.from, tableAliasToken?.to)
        let WhenCommandTokens = StatementToken?.getChild("WhenCommand")
        let FromCommandTokens = StatementToken?.getChild("FromCommand")
        let usedVariablesTokens = StatementToken?.getChild("Scripts")?.getChild("SearchCommand")?.getChildren("Variable")
        let usedAliasTokens = StatementToken?.getChild("Scripts")?.getChild("CalculateCommand")?.getChildren("Alias")
        let commands = []
        let AliasTokensUsedInWhere = StatementToken?.getChild("Scripts")?.getChild("WhereCommand")?.getChildren("Alias")
        let AliasTokensUsedInFetch = StatementToken?.getChild("Scripts")?.getChild("FetchCommand")?.getChildren("Alias")
        let StatementFetchCommand = StatementToken?.getChild("Scripts")?.getChild("FetchCommand")
        let StatementSortCommand = StatementToken?.getChild("Scripts")?.getChild("SortCommand")
        let suggestions = null

        // variables used in SearchCommand
        usedVariablesTokens?.forEach(element => {
          let variableUsed = view.state.sliceDoc(element.from, element.to)
          let respectiveOperatorType = element.prevSibling
          let respectiveFieldType = element.prevSibling?.prevSibling
          let respectiveOperator = view.state.sliceDoc(respectiveOperatorType.from, respectiveOperatorType.to)
          let respectiveField = view.state.sliceDoc(respectiveFieldType.from, respectiveFieldType.to)
          if (!datatypes[respectiveFieldType.node.name]?.includes(variables[variableUsed])) {
            diagnostics.push({
              from: element?.from,
              to: element?.to,
              severity: "error",
              message: `Invalid value '${variableUsed}' for field '${respectiveField}': incompatible type`,
            })
          }
          if (!datatypesOperators[variables[variableUsed]]?.includes(respectiveOperatorType.node.name)) {
            diagnostics.push({
              from: respectiveOperatorType?.from,
              to: respectiveOperatorType?.to,
              severity: "error",
              message: `Invalid operator '${respectiveOperator}' for field '${respectiveField}': incompatible type`,
            })
          }
        })
        //Alias used in Calculate command
        // usedAliasTokens?.forEach(element => {
        // let alias = view.state.sliceDoc(element.from, element.to)
        if (StatementToken?.getChild("Scripts")?.getChild("CalculateCommand")) {
          let alias_list = this.find_alias_name([StatementToken?.getChild("Scripts")?.getChild("CalculateCommand")], view, tableAlias[Alias], mode)
          // let 
          alias_list["alias_names"].forEach(alias => {
            if (field_alias.includes(alias.name)) {
              diagnostics.push({
                from: alias.pos,
                to: alias.name.length,
                severity: "error",
                message: `Duplicate alias ${alias.name}`,
              })
            } else
              field_alias.push(alias.name)

          });
        }
        // });
        //Alias used in Where command
        AliasTokensUsedInWhere?.forEach(element => {
          let alias = view.state.sliceDoc(element.from, element.to)
          if (!field_alias.includes(alias)) {
            diagnostics.push({
              from: element.from,
              to: element.to,
              severity: "error",
              message: `Undefined alias ${alias}`,
            })
          } else
            field_alias.push(alias)
        });
        //Alias used in Fetch command
        AliasTokensUsedInFetch?.forEach(element => {
          let alias = view.state.sliceDoc(element.from, element.to)
          if (!field_alias.includes(alias)) {
            diagnostics.push({
              from: element.from,
              to: element.to,
              severity: "error",
              message: `Undefined alias ${alias}`,
            })
          }
          field_alias.push(alias)
        });
        // undefined variables
// console.log("Alias table ->",tableAlias)
// console.log("Alias  ->",Alias)
        if (!(Alias in tableAlias) && tableAliasToken) {
          diagnostics.push({
            from: tableAliasToken?.from,
            to: tableAliasToken?.to,
            severity: "error",
            message: `Undefined table '${Alias}'`,
          })
        }
        let applicableFields = { "StringFields": {}, "NumericFields": {}, "IpFields": {}, "TimestampFields": {}, "Alias": {} }

        //multi partition is used on a variable
        if ((Alias in tableAlias) && tableAlias[Alias].length > 1) {
          // to store distinct and common fields from all the partitions combined by grouping the  token type
          tableAlias[Alias]?.forEach(partition => {
            // populating common and distinct fields across partitons into  applicableFields
            if (partition in this.sharedService.platform_plugins) {
              this.sharedService.platform_plugins[partition]["platform_fields"]?.forEach(fieldobj => {
                if (fieldobj["display_type"] == "text") {
                  applicableFields["StringFields"][fieldobj["value"]] = fieldobj["value"] in applicableFields["StringFields"] ? applicableFields["StringFields"][fieldobj["value"]] + 1 : 1
                } else if (fieldobj["display_type"] == "number") {
                  applicableFields["NumericFields"][fieldobj["value"]] = fieldobj["value"] in applicableFields["NumericFields"] ? applicableFields["NumericFields"][fieldobj["value"]] + 1 : 1
                } else if (fieldobj["display_type"] == "timestamp") {
                  applicableFields["TimestampFields"][fieldobj["value"]] = fieldobj["value"] in applicableFields["TimestampFields"] ? applicableFields["TimestampFields"][fieldobj["value"]] + 1 : 1
                } else if (fieldobj["display_type"] == "inet") {
                  applicableFields["IpFields"][fieldobj["value"]] = fieldobj["value"] in applicableFields["IpFields"] ? applicableFields["IpFields"][fieldobj["value"]] + 1 : 1
                }
              });
            }
          });
          let fieldsInCommands = {
            "SearchCommand": { "StringFields": {}, "NumericFields": {}, "IpFields": {}, "TimestampFields": {}, "Alias": {} },
            "CalculateCommand": { "StringFields": {}, "NumericFields": {}, "IpFields": {}, "TimestampFields": {}, "Alias": {} },
            "ByCommand": { "StringFields": {}, "NumericFields": {}, "IpFields": {}, "TimestampFields": {}, "Alias": {} },
            "WhereCommand": { "StringFields": {}, "NumericFields": {}, "IpFields": {}, "TimestampFields": {}, "Alias": {} },
          }
          // iterate through commands
          Object.keys(fieldsInCommands).forEach(command => {
            // iterate through types of fields
            Object.keys(fieldsInCommands[command]).forEach(fieldtype => {
              let CommandFields = StatementToken?.getChild("Scripts")?.getChild(command)?.getChildren(fieldtype)
              CommandFields?.forEach(element => {
                let field = view.state.sliceDoc(element.from, element.to)
                if (element.name in applicableFields) {
                  if (applicableFields[element.name][field] < 2) {
                    diagnostics.push({
                      from: element?.from,
                      to: element?.to,
                      severity: "error",
                      message: `Field '${field}' not applicable for table '${Alias}'`,
                    })
                  }
                }
              });

            });


          });
          //check only applicable fields is present in fetch command
          if (!StatementFetchCommand.getChild("Fetchall")) {
            let calculateCommandFields = []
            // any fields other than alias from calculate is wrong
            if (StatementFetchCommand?.prevSibling?.name == 'CalculateCommand') {
              let suggestions = this.find_alias_name([StatementFetchCommand?.prevSibling], view, tableAlias[Alias], mode)
              suggestions["alias_names"].forEach(element => {
                calculateCommandFields.push(element["name"])
              });
              //   let referenceFields = suggestions["alias_names"].concat(suggestions["field_names"])


            }
            else if (StatementFetchCommand?.prevSibling?.name == 'ByCommand') {
              let suggestions = this.find_alias_name([StatementFetchCommand?.prevSibling, StatementFetchCommand?.prevSibling?.prevSibling], view, tableAlias[Alias], mode)
              suggestions["alias_names"].forEach(element => {
                calculateCommandFields.push(element["name"])
              });
              suggestions["field_names"].forEach(element => {
                calculateCommandFields.push(element["name"])
              });
              //   let referenceFields = suggestions["alias_names"].concat(suggestions["field_names"])


            }
            else if (StatementFetchCommand?.prevSibling?.name == 'SearchCommand') {
              let fieldsAvailableForFetch = new Set()
              tableAlias[Alias].forEach(partition => {
                this.sharedService.platform_plugins[partition]["platform_fields"].forEach(flds => {
                  fieldsAvailableForFetch.add(flds["value"])
                });
              });
              calculateCommandFields = Array.from(fieldsAvailableForFetch)

            }
            Object.keys(fieldsInCommands["SearchCommand"]).forEach(fieldtype => {
              let CommandFields = StatementToken?.getChild("Scripts")?.getChild("FetchCommand")?.getChildren(fieldtype)
              CommandFields?.forEach(element => {
                let field = view.state.sliceDoc(element.from, element.to)
                if (!(calculateCommandFields.includes(field))) {
                  diagnostics.push({
                    from: element?.from,
                    to: element?.to,
                    severity: "error",
                    message: `Field '${field}' not applicable for FETCH.`,
                  })
                }
              });
            });
          }
          //check only applicable fields is present in sort command
          if (StatementSortCommand) {
            let calculateCommandFields = []

            if (StatementFetchCommand?.prevSibling?.name == 'CalculateCommand') {
              let suggestions = this.find_alias_name([StatementFetchCommand?.prevSibling], view, tableAlias[Alias], mode)
              suggestions["alias_names"].forEach(element => {
                calculateCommandFields.push(element["name"])
              });
              //   let referenceFields = suggestions["alias_names"].concat(suggestions["field_names"])


            }
            else if (StatementFetchCommand?.prevSibling?.name == 'ByCommand') {
              let suggestions = this.find_alias_name([StatementFetchCommand?.prevSibling, StatementFetchCommand?.prevSibling?.prevSibling], view, tableAlias[Alias], mode)
              suggestions["alias_names"].forEach(element => {
                calculateCommandFields.push(element["name"])
              });
              suggestions["field_names"].forEach(element => {
                calculateCommandFields.push(element["name"])
              });
              //   let referenceFields = suggestions["alias_names"].concat(suggestions["field_names"])


            }
            else if (StatementFetchCommand?.prevSibling?.name == 'SearchCommand') {
              let fieldsAvailableForFetch = new Set()
              tableAlias[Alias].forEach(partition => {
                this.sharedService.platform_plugins[partition]["platform_fields"].forEach(flds => {
                  fieldsAvailableForFetch.add(flds["value"])
                });
              });
              calculateCommandFields = Array.from(fieldsAvailableForFetch)

            }
            Object.keys(fieldsInCommands["SearchCommand"]).forEach(fieldtype => {
              let CommandFields = StatementToken?.getChild("Scripts")?.getChild("SortCommand")?.getChildren(fieldtype)
              CommandFields?.forEach(element => {
                let field = view.state.sliceDoc(element.from, element.to)
                if (!(calculateCommandFields.includes(field))) {
                  diagnostics.push({
                    from: element?.from,
                    to: element?.to,
                    severity: "error",
                    message: `Field '${field}' not applicable for SORT.`,
                  })
                }
              });
            });
          }
          // let SearchCommandAliasFields = StatementToken?.getChild("Scripts")?.getChild("SearchCommand")?.getChildren("Alias")

        }
        let fieldsInCommands = {
          "SearchCommand": { "StringFields": {}, "NumericFields": {}, "IpFields": {}, "TimestampFields": {}, "Alias": {} },
          "CalculateCommand": { "StringFields": {}, "NumericFields": {}, "IpFields": {}, "TimestampFields": {}, "Alias": {} },
          "ByCommand": { "StringFields": {}, "NumericFields": {}, "IpFields": {}, "TimestampFields": {}, "Alias": {} },
          "WhereCommand": { "StringFields": {}, "NumericFields": {}, "IpFields": {}, "TimestampFields": {}, "Alias": {} },
        }
        // iterate through commands
        Object.keys(fieldsInCommands).forEach(command => {
          // iterate through types of fields
          Object.keys(fieldsInCommands[command]).forEach(fieldtype => {
            let CommandFields = StatementToken?.getChild("Scripts")?.getChild(command)?.getChildren(fieldtype)
            CommandFields?.forEach(element => {
              let field = view.state.sliceDoc(element.from, element.to)
              if (element.name in applicableFields) {
                if (applicableFields[element.name][field] < 2) {
                  diagnostics.push({
                    from: element?.from,
                    to: element?.to,
                    severity: "error",
                    message: `Field '${field}' not applicable for table '${Alias}'`,
                  })
                }
              }
            });

          });


        });
        //check only applicable fields is present in fetch command
        if (!StatementFetchCommand.getChild("Fetchall")) {
          // console.log("Not Fetch all")
          let calculateCommandFields = []
          // any fields other than alias from calculate is wrong
          if (StatementFetchCommand?.prevSibling?.name == 'CalculateCommand') {
// console.log("Calculate command")
            let suggestions = this.find_alias_name([StatementFetchCommand?.prevSibling], view, tableAlias[Alias], mode)
            // console.log("Alias=->",suggestions)
            suggestions["alias_names"].forEach(element => {
              calculateCommandFields.push(element["name"])
            });
            //   let referenceFields = suggestions["alias_names"].concat(suggestions["field_names"])


          }
          else if (StatementFetchCommand?.prevSibling?.name == 'ByCommand') {
            let suggestions = this.find_alias_name([StatementFetchCommand?.prevSibling, StatementFetchCommand?.prevSibling?.prevSibling], view, tableAlias[Alias], mode)
            suggestions["alias_names"].forEach(element => {
              calculateCommandFields.push(element["name"])
            });
            suggestions["field_names"].forEach(element => {
              calculateCommandFields.push(element["name"])
            });
            //   let referenceFields = suggestions["alias_names"].concat(suggestions["field_names"])


          }
          else if (StatementFetchCommand?.prevSibling?.name == 'WhereCommand') {
            let suggestions = this.find_alias_name([StatementFetchCommand?.prevSibling?.prevSibling?.prevSibling, StatementFetchCommand?.prevSibling?.prevSibling], view, tableAlias[Alias], mode)
            suggestions["alias_names"].forEach(element => {
              calculateCommandFields.push(element["name"])
            });
            suggestions["field_names"].forEach(element => {
              calculateCommandFields.push(element["name"])
            });
            //   let referenceFields = suggestions["alias_names"].concat(suggestions["field_names"])


          }
          else if (StatementFetchCommand?.prevSibling?.name == 'SearchCommand') {
            let fieldsAvailableForFetch = new Set()
            tableAlias[Alias].forEach(partition => {
              this.sharedService.platform_plugins[partition]["platform_fields"].forEach(flds => {
                fieldsAvailableForFetch.add(flds["value"])
              });
            });
            calculateCommandFields = Array.from(fieldsAvailableForFetch)

          }
          Object.keys(fieldsInCommands["SearchCommand"]).forEach(fieldtype => {
            let CommandFields = StatementToken?.getChild("Scripts")?.getChild("FetchCommand")?.getChildren(fieldtype)
            CommandFields?.forEach(element => {
              let field = view.state.sliceDoc(element.from, element.to)
              if (!(calculateCommandFields.includes(field))) {
                // console.log("Calculate =---->",calculateCommandFields)
                diagnostics.push({
                  from: element?.from,
                  to: element?.to,
                  severity: "error",
                  message: `Field '${field}' not applicable for FETCH.`,
                })
              }
            });
          });
        }
        //check only applicable fields is present in sort command
        if (StatementSortCommand) {
          let calculateCommandFields = []

          if (StatementFetchCommand?.prevSibling?.name == 'CalculateCommand') {
            let suggestions = this.find_alias_name([StatementFetchCommand?.prevSibling], view, tableAlias[Alias], mode)
            suggestions["alias_names"].forEach(element => {
              calculateCommandFields.push(element["name"])
            });
            //   let referenceFields = suggestions["alias_names"].concat(suggestions["field_names"])


          }
          else if (StatementFetchCommand?.prevSibling?.name == 'ByCommand') {
            let suggestions = this.find_alias_name([StatementFetchCommand?.prevSibling, StatementFetchCommand?.prevSibling?.prevSibling], view, tableAlias[Alias], mode)
            suggestions["alias_names"].forEach(element => {
              calculateCommandFields.push(element["name"])
            });
            suggestions["field_names"].forEach(element => {
              calculateCommandFields.push(element["name"])
            });
            //   let referenceFields = suggestions["alias_names"].concat(suggestions["field_names"])


          }
          else if (StatementFetchCommand?.prevSibling?.name == 'SearchCommand') {
            let fieldsAvailableForFetch = new Set()
            tableAlias[Alias].forEach(partition => {
              this.sharedService.platform_plugins[partition]["platform_fields"].forEach(flds => {
                fieldsAvailableForFetch.add(flds["value"])
              });
            });
            calculateCommandFields = Array.from(fieldsAvailableForFetch)

          }
          Object.keys(fieldsInCommands["SearchCommand"]).forEach(fieldtype => {
            let CommandFields = StatementToken?.getChild("Scripts")?.getChild("SortCommand")?.getChildren(fieldtype)
            CommandFields?.forEach(element => {
              let field = view.state.sliceDoc(element.from, element.to)
              if (!(calculateCommandFields.includes(field))) {
                diagnostics.push({
                  from: element?.from,
                  to: element?.to,
                  severity: "error",
                  message: `Field '${field}' not applicable for SORT.`,
                })
              }
            });
          });
        }
      });
      JoinCommand?.forEach(command => {
        let variablesUsedInJoinCommand = {}
        command.getChildren("Variable")?.forEach((token) => {
          let Variable = view.state.sliceDoc(token.node.from, token.node.to)
          if (variables[Variable] != "Statement") {
            diagnostics.push({
              from: token.node.from,
              to: token.node.to,
              severity: "error",
              message: `Undefined variable '${Variable}'.`,
            })
          }
          // alias which is not present in the declared variable statement
          if (token?.nextSibling.name == "Dot" && token?.nextSibling?.nextSibling.name == "Alias") {
            let AliasUsedToken = token?.nextSibling?.nextSibling
            let AliasUsed = view.state.sliceDoc(AliasUsedToken.from, AliasUsedToken.to)
            if (!field_alias[Variable].includes(AliasUsed)) {
              diagnostics.push({
                from: AliasUsedToken.from,
                to: AliasUsedToken.to,
                severity: "error",
                message: `Field '${AliasUsed}' is not available in variable '${Variable}'.`,
              })
            }
          }
          // field which is not present in the declared variable statement
          if (token?.nextSibling.name == "Dot" && token?.nextSibling?.nextSibling.name != "Alias") {
            let FieldsedToken = token?.nextSibling?.nextSibling
            let FieldsUsed = view.state.sliceDoc(FieldsedToken.from, FieldsedToken.to)
            if (!variable_fields[Variable].includes(FieldsUsed)) {
              diagnostics.push({
                from: FieldsedToken.from,
                to: FieldsedToken.to,
                severity: "error",
                message: `Field '${FieldsUsed}' is not available in variable '${Variable}'.`,
              })
            }
          }
          // cannot join same table
          if (Variable in variablesUsedInJoinCommand && token?.prevSibling.name == "Join") {
            diagnostics.push({
              from: token.node.from,
              to: token.node.to,
              severity: "error",
              message: `Cannot JOIN same table '${Variable}' .`,
            })
          }
          // same table cannot be on both LHS and RHS
          if (token?.nextSibling?.name == "Dot" && ["GenericOperator", "NumericOperator"].includes(token?.nextSibling?.nextSibling?.nextSibling?.name)) {
            let RHSVariableToken = token?.nextSibling?.nextSibling?.nextSibling?.nextSibling
            let RHSVariable = RHSVariableToken ? view.state.sliceDoc(RHSVariableToken?.from, RHSVariableToken?.to) : null
            if (Variable == RHSVariable) {
              diagnostics.push({
                from: RHSVariableToken.from,
                to: RHSVariableToken.to,
                severity: "error",
                message: `Cannot JOIN same table '${RHSVariable}' .`,
              })
            }
          }
          variablesUsedInJoinCommand[Variable] = []

        })

      });
      FilterCommand?.forEach(command => {
        // filter conditions with fields
        command?.getChildren("Variable")?.forEach((token) => {
          let Variable = view.state.sliceDoc(token.node.from, token.node.to)
          if (variables[Variable] != "Statement") {
            diagnostics.push({
              from: token.node.from,
              to: token.node.to,
              severity: "error",
              message: `Undefined variable '${Variable}'.`,
            })
          }
          // alias which is not present in the declared variable statement
          if (token?.nextSibling?.name == "Dot" && token?.nextSibling?.nextSibling?.name == "Alias") {
            let AliasUsedToken = token?.nextSibling?.nextSibling
            let AliasUsed = view.state.sliceDoc(AliasUsedToken.from, AliasUsedToken.to)
            if (!field_alias[Variable].includes(AliasUsed)) {
              diagnostics.push({
                from: AliasUsedToken.from,
                to: AliasUsedToken.to,
                severity: "error",
                message: `Field '${AliasUsed}' is not available in variable '${Variable}'.`,
              })
            }
          }
          // field which is not present in the declared variable statement
          if (token?.nextSibling?.name == "Dot" && token?.nextSibling?.nextSibling?.name != "Alias") {
            let FieldsUsedToken = token?.nextSibling?.nextSibling
            let FieldsUsed = view.state.sliceDoc(FieldsUsedToken.from, FieldsUsedToken.to)
            if (!variable_fields[Variable].includes(FieldsUsed)) {
              diagnostics.push({
                from: FieldsUsedToken.from,
                to: FieldsUsedToken.to,
                severity: "error",
                message: `Field '${FieldsUsed}' is not available in variable '${Variable}'.`,
              })
            }
          }

          // same table cannot be on both LHS and RHS
          if (token?.nextSibling?.name == "Dot" && ["GenericOperator", "NumericOperator"].includes(token?.nextSibling?.nextSibling?.nextSibling?.name)) {
            let RHSVariableToken = token?.nextSibling?.nextSibling?.nextSibling?.nextSibling
            let RHSVariable = RHSVariableToken ? view.state.sliceDoc(RHSVariableToken?.from, RHSVariableToken?.to) : null
            if (Variable == RHSVariable) {
              diagnostics.push({
                from: RHSVariableToken.from,
                to: RHSVariableToken.to,
                severity: "error",
                message: `Filters cannot have same table '${RHSVariable}' on both sides.`,
              })
            }
          }
        })
        // filter conditions with arithmatic expressions
        command?.getChildren("ArithmaticExpression")?.forEach(child => {
          child?.getChildren("Variable")?.forEach((token) => {
            let Variable = view.state.sliceDoc(token.from, token.to)
            if (variables[Variable] != "Statement") {
              diagnostics.push({
                from: token.node.from,
                to: token.node.to,
                severity: "error",
                message: `Undefined variable '${Variable}'.`,
              })
            }
            // alias which is not present in the declared variable statement
            if (token?.nextSibling?.name == "Dot" && token?.nextSibling?.nextSibling?.name == "Alias") {
              let AliasUsedToken = token?.nextSibling?.nextSibling
              let AliasUsed = view.state.sliceDoc(AliasUsedToken.from, AliasUsedToken.to)
              if (Variable in field_alias && !field_alias[Variable].includes(AliasUsed)) {
                diagnostics.push({
                  from: AliasUsedToken.from,
                  to: AliasUsedToken.to,
                  severity: "error",
                  message: `Field '${AliasUsed}' is not available in variable '${Variable}'.`,
                })
              }
            }
          })
        });
      });
      FetchCommand?.forEach(command => {
        // filter conditions with fields
        command?.getChildren("Variable")?.forEach((token) => {
          let Variable = view.state.sliceDoc(token.node.from, token.node.to)
          if (variables[Variable] != "Statement") {
            diagnostics.push({
              from: token.node.from,
              to: token.node.to,
              severity: "error",
              message: `Undefined variable '${Variable}'.`,
            })
          }
          // alias which is not present in the declared variable statement
          if (token?.nextSibling.name == "Dot" && token?.nextSibling?.nextSibling.name == "Alias") {
            let AliasUsedToken = token?.nextSibling?.nextSibling
            let AliasUsed = view.state.sliceDoc(AliasUsedToken.from, AliasUsedToken.to)
            if (!field_alias[Variable].includes(AliasUsed)) {
              diagnostics.push({
                from: AliasUsedToken.from,
                to: AliasUsedToken.to,
                severity: "error",
                message: `Field '${AliasUsed}' is not available in variable '${Variable}'.`,
              })
            }
          }
          // field which is not present in the declared variable statement
          if (token?.nextSibling.name == "Dot" && token?.nextSibling?.nextSibling.name != "Alias") {
            let FieldsUsedToken = token?.nextSibling?.nextSibling
            let FieldsUsed = view.state.sliceDoc(FieldsUsedToken.from, FieldsUsedToken.to)
            if (!variable_fields[Variable].includes(FieldsUsed)) {
              diagnostics.push({
                from: FieldsUsedToken.from,
                to: FieldsUsedToken.to,
                severity: "error",
                message: `Field '${FieldsUsed}' is not available in variable '${Variable}'.`,
              })
            }
          }
        })
        // filter conditions with arithmatic expressions
        command?.getChildren("ArithmaticExpression")?.forEach(child => {
          child?.getChildren("Variable")?.forEach((token) => {
            let Variable = view.state.sliceDoc(token.from, token.to)
            if (variables[Variable] != "Statement") {
              diagnostics.push({
                from: token.node.from,
                to: token.node.to,
                severity: "error",
                message: `Undefined variable '${Variable}'.`,
              })
            }
            // alias which is not present in the declared variable statement
            if (token?.nextSibling.name == "Dot" && token?.nextSibling?.nextSibling.name == "Alias") {
              let AliasUsedToken = token?.nextSibling?.nextSibling
              let AliasUsed = view.state.sliceDoc(AliasUsedToken.from, AliasUsedToken.to)
              if (Variable in field_alias && !field_alias[Variable].includes(AliasUsed)) {
                diagnostics.push({
                  from: AliasUsedToken.from,
                  to: AliasUsedToken.to,
                  severity: "error",
                  message: `Field '${AliasUsed}' is not available in variable '${Variable}'.`,
                })
              }
            }
          })
        });
      });
    }
    catch (error) {
//console.log(error)
    }
    // });
    syntaxTree(view.state).cursor().iterate(node => {
      if (node.type.isError) {
        diagnostics.push({
          from: node.from,
          to: node.to,
          severity: "error",
          message: "syntax invalid",
        })
      }
    })
    return diagnostics
  }

  csiem_query_lint(view) {
    let diagnostics = []
    let cursor = syntaxTree(view.state).cursor()
    do {
    } while (cursor.next())
    let variables = {}
    let ImportCommand = syntaxTree(view.state).cursor().node.getChildren("ImportCommand")
    let LetCommand = syntaxTree(view.state).cursor().node.getChildren("LetCommand")
    let Statement = syntaxTree(view.state).cursor().node.getChild("Statement")
    let JoinCommand = syntaxTree(view.state).cursor().node.getChild("JoinCommand")
    // LetCommand.forEach(command => {
    //   // .forEach((element) => {

    //   let element = command.getChild("Variable")?.node
    //   let variable = view.state.sliceDoc(element.from, element.to)
    //   let variableType = element.nextSibling?.nextSibling?.name
    //   // console.log(variable, variableType)
    //   variables[variable] = variableType
    //   // })
    // });
    // console.table(variables)
    // let CalculateCommand = syntaxTree(view.state).cursor().node.getChild("CalculateCommand")
    // let ByCommand = syntaxTree(view.state).cursor().node.getChild("ByCommand")
    // let FetchCommand = syntaxTree(view.state).cursor().node.getChild("FetchCommand")
    // let WhereCommand = syntaxTree(view.state).cursor().node.getChild("WhereCommand")
    // let SortCommand = syntaxTree(view.state).cursor().node.getChild("SortCommand")
    // let LimitCommand = syntaxTree(view.state).cursor().node.getChild("LimitCommand")

    // if (FetchCommand && (CalculateCommand || ByCommand)) {
    //   let suggestions = CalculateCommand ? this.find_alias_name([CalculateCommand], view) : { alias_names: [], field_names: [] }
    //   let suggestions1 = ByCommand ? this.find_alias_name([ByCommand], view) : { alias_names: [], field_names: [] }
    //   let referenceFields = suggestions["alias_names"].concat(suggestions["field_names"], suggestions1["alias_names"], suggestions1["field_names"])

    //   let ClauseFields = FetchCommand.node.getChildren("Alias").concat(FetchCommand.node.getChildren("StringFields"), FetchCommand.node.getChildren("NumericFields"), FetchCommand.node.getChildren("IpFields"), FetchCommand.node.getChildren("ArithmaticExpression"), FetchCommand.node.getChildren("StringFunctionType1"), FetchCommand.node.getChildren("StringFunctionType2"), FetchCommand.node.getChildren("StringFunctionType3"), FetchCommand.node.getChildren("StringFunctionType4"), FetchCommand.node.getChildren("StringFunctionType5"), FetchCommand.node.getChildren("StringFunctionType6"), FetchCommand.node.getChildren("NumberFunctionType1"), FetchCommand.node.getChildren("NumberFunctionType2"), FetchCommand.node.getChildren("NumberFunctionType3"), FetchCommand.node.getChildren("TimestampFunctionType1"), FetchCommand.node.getChildren("CommonFunctionType1"), FetchCommand.node.getChildren("CommonFunctionType2"), FetchCommand.node.getChildren("ListFunctionType1"), FetchCommand.node.getChildren("ListFunctionType2"), FetchCommand.node.getChildren("ListFunctionType3"))
    //   var valuesSoFar = Object.create(null);

    //   ClauseFields.forEach(element => {
    //     // let word = view.state.sliceDoc(element.from, element.to).replace(/['"]+/g, '')
    //     let word = view.state.sliceDoc(element.from, element.to)


    //     const exists = referenceFields.some(obj => obj.name === word);
    //     if (!exists) {

    //       diagnostics.push({
    //         from: element.from, to: element.to, severity: "error", message: "Field specified not in BY clause."
    //       })
    //     }
    //     if (word in valuesSoFar) {

    //       diagnostics.push({
    //         from: element.from, to: element.to, severity: "error", message: "Duplicate field in FETCH clause"
    //       })
    //     }


    //     valuesSoFar[word] = true;

    //   });

    // }
    // if (ByCommand && CalculateCommand) {
    //   let suggestions = CalculateCommand ? this.find_alias_name([CalculateCommand], view) : { alias_names: [], field_names: [] }
    //   let referenceFields = suggestions["alias_names"].concat(suggestions["field_names"])

    //   let ClauseFields = ByCommand.node.getChildren("Alias").concat(ByCommand.node.getChildren("StringFields"), ByCommand.node.getChildren("NumericFields"), ByCommand.node.getChildren("IpFields"), ByCommand.node.getChildren("ArithmaticExpression"), ByCommand.node.getChildren("StringFunctionType1"), ByCommand.node.getChildren("StringFunctionType2"), ByCommand.node.getChildren("StringFunctionType3"), ByCommand.node.getChildren("StringFunctionType4"), ByCommand.node.getChildren("StringFunctionType5"), ByCommand.node.getChildren("StringFunctionType6"), ByCommand.node.getChildren("NumberFunctionType1"), ByCommand.node.getChildren("NumberFunctionType2"), ByCommand.node.getChildren("NumberFunctionType3"), ByCommand.node.getChildren("TimestampFunctionType1"), ByCommand.node.getChildren("CommonFunctionType1"), ByCommand.node.getChildren("CommonFunctionType2"), ByCommand.node.getChildren("ListFunctionType1"), ByCommand.node.getChildren("ListFunctionType2"), ByCommand.node.getChildren("ListFunctionType3"))
    //   var valuesSoFar = Object.create(null);
    //   ClauseFields.forEach(element => {

    //     // if (!tableFields.includes(view.state.sliceDoc(element.from, element.to).replace(/['"]+/g, ''))) {

    //     //   diagnostics.push({
    //     //     from: element.from, to: element.to, severity: "error", message: "Column specified not in BY clause."
    //     //   })
    //     // }
    //     let word = view.state.sliceDoc(element.from, element.to)

    //     if (word in valuesSoFar) {

    //       diagnostics.push({
    //         from: element.from, to: element.to, severity: "error", message: "Duplicate field in BY clause"
    //       })
    //     }

    //     valuesSoFar[word] = true;

    //   });

    // }

    // if (SortCommand) {
    //   let fetchall = FetchCommand.node.getChild("Fetchall")
    //   if (FetchCommand) {
    //     let suggestions: any = this.find_alias_name([FetchCommand], view)
    //     let suggestion1: any = { alias_names: [], field_names: [] }
    //     if (fetchall) {
    //       suggestions = CalculateCommand ? this.find_alias_name([CalculateCommand], view) : this.find_alias_name([FetchCommand], view)
    //       suggestion1 = ByCommand ? this.find_alias_name([ByCommand], view) : { alias_names: [], field_names: [] }
    //     }
    //     // else {
    //     //   suggestions = this.find_alias_name([FetchCommand], view)
    //     // }

    //     let referenceFields = suggestions["alias_names"].concat(suggestions["field_names"], suggestion1["alias_names"], suggestion1["field_names"])
    //     let ClauseFields = SortCommand.node.getChildren("Alias").concat(SortCommand.node.getChildren("StringFields"), SortCommand.node.getChildren("NumericFields"), SortCommand.node.getChildren("IpFields"), SortCommand.node.getChildren("ArithmaticExpression"), SortCommand.node.getChildren("StringFunctionType1"), SortCommand.node.getChildren("StringFunctionType2"), SortCommand.node.getChildren("StringFunctionType3"), SortCommand.node.getChildren("StringFunctionType4"), SortCommand.node.getChildren("StringFunctionType5"), SortCommand.node.getChildren("StringFunctionType6"), SortCommand.node.getChildren("NumberFunctionType1"), SortCommand.node.getChildren("NumberFunctionType2"), SortCommand.node.getChildren("NumberFunctionType3"), SortCommand.node.getChildren("TimestampFunctionType1"), SortCommand.node.getChildren("CommonFunctionType1"), SortCommand.node.getChildren("CommonFunctionType2"), SortCommand.node.getChildren("ListFunctionType1"), SortCommand.node.getChildren("ListFunctionType2"), SortCommand.node.getChildren("ListFunctionType3"))
    //     var valuesSoFar = Object.create(null);
    //     ClauseFields.forEach(element => {
    //       let word = view.state.sliceDoc(element.from, element.to)

    //       const exists = referenceFields.some(obj => obj.name === word);
    //       if (!exists) {

    //         diagnostics.push({
    //           from: element.from, to: element.to, severity: "error", message: `Column ${word} not in FETCH clause.`
    //         })
    //       }
    //       if (word in valuesSoFar) {

    //         diagnostics.push({
    //           from: element.from, to: element.to, severity: "error", message: "Duplicate field in SORT clause"
    //         })
    //       }
    //       valuesSoFar[word] = true;

    //     });

    //   }
    //   else if (CalculateCommand || ByCommand) {
    //     let suggestions = CalculateCommand ? this.find_alias_name([CalculateCommand], view) : { alias_names: [], field_names: [] }
    //     let suggestions1 = ByCommand ? this.find_alias_name([ByCommand], view) : { alias_names: [], field_names: [] }
    //     let referenceFields = suggestions["alias_names"].concat(suggestions["field_names"], suggestions1["alias_names"], suggestions1["field_names"])


    //     let ClauseFields = SortCommand.node.getChildren("Alias").concat(SortCommand.node.getChildren("StringFields"), SortCommand.node.getChildren("NumericFields"), SortCommand.node.getChildren("IpFields"), SortCommand.node.getChildren("ArithmaticExpression"), SortCommand.node.getChildren("StringFunctionType1"), SortCommand.node.getChildren("StringFunctionType2"), SortCommand.node.getChildren("StringFunctionType3"), SortCommand.node.getChildren("StringFunctionType4"), SortCommand.node.getChildren("StringFunctionType5"), SortCommand.node.getChildren("StringFunctionType6"), SortCommand.node.getChildren("NumberFunctionType1"), SortCommand.node.getChildren("NumberFunctionType2"), SortCommand.node.getChildren("NumberFunctionType3"), SortCommand.node.getChildren("TimestampFunctionType1"), SortCommand.node.getChildren("CommonFunctionType1"), SortCommand.node.getChildren("CommonFunctionType2"), SortCommand.node.getChildren("ListFunctionType1"), SortCommand.node.getChildren("ListFunctionType2"), SortCommand.node.getChildren("ListFunctionType3"))
    //     var valuesSoFar = Object.create(null);
    //     ClauseFields.forEach(element => {
    //       let word = view.state.sliceDoc(element.from, element.to)
    //       const exists = referenceFields.some(obj => obj.name === word);
    //       if (!exists) {

    //         diagnostics.push({
    //           from: element.from, to: element.to, severity: "error", message: `Column ${word} not in BY clause.`
    //         })
    //       }
    //       if (word in valuesSoFar) {

    //         diagnostics.push({
    //           from: element.from, to: element.to, severity: "error", message: "Duplicate field in SORT clause"
    //         })
    //       }
    //       valuesSoFar[word] = true;

    //     });
    //   }


    // }
    // if (FetchCommand && !(CalculateCommand || ByCommand)) {
    //   // let referenceFields = []
    //   // this.platform_fields.forEach(item => {
    //   //   referenceFields.push({ name: item.value })
    //   // })

    //   let ClauseFields = FetchCommand.node.getChildren("Alias").concat(FetchCommand.node.getChildren("NumericFields"), FetchCommand.node.getChildren("IpFields"), FetchCommand.node.getChildren("ArithmaticExpression"), FetchCommand.node.getChildren("StringFunctionType1"), FetchCommand.node.getChildren("StringFunctionType2"), FetchCommand.node.getChildren("StringFunctionType3"), FetchCommand.node.getChildren("StringFunctionType4"), FetchCommand.node.getChildren("StringFunctionType5"), FetchCommand.node.getChildren("StringFunctionType6"), FetchCommand.node.getChildren("NumberFunctionType1"), FetchCommand.node.getChildren("NumberFunctionType2"), FetchCommand.node.getChildren("NumberFunctionType3"), FetchCommand.node.getChildren("TimestampFunctionType1"), FetchCommand.node.getChildren("CommonFunctionType1"), FetchCommand.node.getChildren("CommonFunctionType2"), FetchCommand.node.getChildren("ListFunctionType1"), FetchCommand.node.getChildren("ListFunctionType2"), FetchCommand.node.getChildren("ListFunctionType3"))
    //   var valuesSoFar = Object.create(null);

    //   ClauseFields.forEach(element => {
    //     // let word = view.state.sliceDoc(element.from, element.to).replace(/['"]+/g, '')
    //     let word = view.state.sliceDoc(element.from, element.to)

    //     if (element.name == "Alias") {

    //       diagnostics.push({
    //         from: element.from, to: element.to, severity: "error", message: `${word} is not a valid field.`
    //       })
    //     }
    //     if (word in valuesSoFar) {

    //       diagnostics.push({
    //         from: element.from, to: element.to, severity: "error", message: "Duplicate field in FETCH clause"
    //       })
    //     }


    //     valuesSoFar[word] = true;

    //   });

    // }
    syntaxTree(view.state).cursor().iterate(node => {
      if (node.type.isError) diagnostics.push({
        from: node.from,
        to: node.to,
        severity: "error",
        message: "syntax invalid",
      })

    })

    return diagnostics
  }

  // these are working version

  isIgnorableNode(node) {
    if (!node) return true;
    const n = node.name;
    return (
      n === "⚠" ||
      n === "Error" ||
      n === "Space" ||
      n === "Whitespace" ||
      n === "Comment" ||
      n === "Punctuation"
    );
  }

  /**
   * Find innermost node covering the position
   */
  //original
  getInnermostNode(state, pos) {
    const tree = syntaxTree(state);
    let node = tree.resolve(pos, -1);
    let found = node;

    if (node.name == "LetCommand" || node.name == "HostScripts") {
      let subscript = node.getChild('Statement')?.getChild('Scripts')?.cursor()
      if (subscript && subscript.from <= pos) {
        if (subscript.node.lastChild.name != "⚠") {
          found = subscript.node.lastChild
        } else {
          found = subscript.node.lastChild.prevSibling
        }
      }
    }
    return found;
  }
  /**
   * Find first non-ignorable sibling before given node
   */
  findPreviousSibling(node, pos) {
    let found_prev = false

    // If the current node ends after pos, go to previous sibling
    if (node.parent && !node.firstChild) {
      if (node.from < pos && node.prevSibling) {
        node = node.prevSibling;
        found_prev = true;
      }

    }
    for (let child = node.firstChild; child; child = child.nextSibling) {
      if ((child.from < pos && child.to < pos) && child.name != "⚠") {
        node = child
        found_prev = true;
      } else {
        break
      }
    }
    if (!found_prev) node = null;
    return node;

  }

  findPreviousSiblingAlert(node, pos) {
    let found_prev = false
    // If the current node ends after pos, go to previous sibling
    if (node.parent && !node.firstChild) {
      if (node.from < pos && node.prevSibling) {
        node = node.prevSibling;
        found_prev = true;
      }

    }

    for (let child = node.firstChild; child; child = child.nextSibling) {
      if (child.from < pos && child.to < pos) {
        node = child
        found_prev = true;
      } else {
        break
      }
    }
    if (!found_prev) node = null;

    return node;

  }

  findPreviousSiblingbefore(node) {
    if (node) {
      if (node.prevSibling) {
        node = node.prevSibling;
      } else if (node.parent) {
        node = node.parent?.prevSibling;
      }
    }

    return node
  }

  findNextSibling(node, pos) {
    let next = null;

    let found_next = false;
    let ignorable_node = null;

    if (node.parent && !node.firstChild) {
      if (node.from < pos && node.nextSibling) {
        next = node.nextSibling;
        found_next = true;
      }

    }

    for (let child = node.firstChild; child; child = child.nextSibling) {
      if (child.from >= pos) {
        next = child
        found_next = true;
        while (next.firstChild) next = next.firstChild;
        break
      }
    }

    while (next && this.isIgnorableNode(next)) {
      if (next.nextSibling) {
        next = next.nextSibling;
        found_next = true;
        while (next.firstChild) next = next.firstChild;
      } else if (next.parent) {
        let parent = next.parent;
        next = next.parent.nextSibling;

        if (next) {
          while (next.firstChild) {
            next = next.firstChild;
            found_next = true;
          }
        } else {
          ignorable_node = parent.lastChild;
        }

      } else {
        ignorable_node = next;
        next = null;

      }
    }

    if (!found_next) next = null;

    return next || ignorable_node;
  }

  findNextSiblingAfter(node) {
    if (node) {
      node = node.nextSibling;
    }

    return node
  }

  /**
   * Get node context: previous, current, next, and parent.
   * Uses the parent relationship of the current node to find neighbors.
   */
  getContextNodes(state, pos, mode) {
    const current = this.getInnermostNode(state, pos);
    if (!current) return { previous: null, current: null, next: null, parent: null };
    const parent = current.parent || current;
    let previous = null;
    let next = null;

    previous = mode == "alert" ? this.findPreviousSiblingAlert(current, pos) : this.findPreviousSibling(current, pos);
    next = this.findNextSibling(current, pos);

    let previous_before = null;
    previous_before = this.findPreviousSiblingbefore(previous)

    let next_after = null;
    next_after = this.findNextSiblingAfter(next)

    let previous_sibling = null;
    previous_sibling = current?.prevSibling

    return { previous, previous_before, current, next, next_after, parent, previous_sibling };
  }


  suggest_group_by_fields(context, previous_sibling, fetchall, platform, platform_plugins, editormode) {
    let suggestions = this.find_alias_name(previous_sibling, context, platform, editormode)
    let groupby_fields = suggestions["field_names"].map((fld) => {
      return { label: fld.name, type: fld.type, boost: 69, snippet: " #{1:}" }
    })
    return groupby_fields
  }

  suggest_fetch_variable_fields(context, previous_sibling, fetchall, platform, editormode) {
    let suggestions = this.find_alias_name(previous_sibling, context, platform, editormode)
    let fields = suggestions["field_names"].map((fld) => {
      return { label: fld.name, type: fld.type, boost: 69, snippet: " #{1:}" }
    })

    let aliasnames = suggestions["alias_names"].map((fld) => {
      return { label: fld.name, type: "alias_name", boost: 68, snippet: " #{1:}" }
    })
    return fields.concat(aliasnames)
  }

  suggest_fetch_variable_fields_withoutspace(context, previous_sibling, fetchall, platform, editormode) {
    let suggestions = this.find_alias_name(previous_sibling, context, platform, editormode)
    let fields = suggestions["field_names"].map((fld) => {
      return { label: fld.name, type: fld.type, boost: 69, snippet: "#{1:}" }
    })

    let aliasnames = suggestions["alias_names"].map((fld) => {
      return { label: fld.name, type: "alias_name", boost: 68, snippet: "#{1:}" }
    })
    return fields.concat(aliasnames)
  }

  suggest_for_alias(context, previous_sibling, fetchall, platform, platform_plugins, editormode) {
    let alias = this.find_current_alias_name(previous_sibling, context, platform, editormode)
    return [{ label: "as", type: "keyword", boost: 76, snippet: ` #{1:${alias['name']}}`, quoats: "" }]
  }

  suggest_alias_names(context, previous_sibling, fetchall, platform, platform_plugins, editormode) {
    let suggestions = this.find_alias_name(previous_sibling, context, platform, editormode)
    let alias_names = suggestions["alias_names"].map((fld) => {
      return { label: fld.name, type: "alias_name", boost: 68, snippet: " #{1:}" }
    })

    return alias_names
  }

  suggest_after_sort(context, previous_sibling, fetchall, platform, platform_plugins, editormode) {
    let statsCmd = [];
    if (fetchall) {
      statsCmd = previous_sibling
    } else {
      let fetch_cmd = syntaxTree(context.state).cursor().node.getChild("FetchCommand");
      if (fetch_cmd) {
        statsCmd.push(fetch_cmd)
      }
    }
    let suggestions = this.find_alias_name(statsCmd, context, platform, editormode)
    let fetch_fields = suggestions["field_names"].map((fld) => {
      return { label: fld.name, type: fld.type, boost: 69, snippet: " #{1:}" }
    })
    let fetch_alias_names = suggestions["alias_names"].map((fld) => {
      return { label: fld.name, type: "alias_name", boost: 68, snippet: " #{1:}" }
    })

    return fetch_fields.concat(fetch_alias_names)
  }

  suggest_variable_name(context, previous_sibling, fetchall, platform) {
    let LetCommandChilds = syntaxTree(context.state).cursor().node.getChildren("LetCommand")
    let variable_name = "";
    LetCommandChilds.forEach((letchild, letind) => {
      if (letchild.from == previous_sibling[0].from) {
        variable_name = "V" + (letind + 1)
      }
    })
    return [{ label: variable_name, type: "variable", boost: 70, snippet: "#{1:}" }]
  }

  getTableAliasName(context, previous_sibling, fetchal, platforml, platform_plugins, editormode) {
    let table_alias = Object.keys(this.findPlatformAliasFromImport(context)).map((alias) => {
      return { label: alias, type: "alias_name", boost: 68, snippet: " #{1:}" }
    })
    return table_alias
  }

  getVariables(context, previous_sibling, fetchall, platform, platform_plugins, editormode) {
    let variables = Object.values(this.letVariableMap(context))
      .filter((variableobj) => variableobj["statement"])
      .map((obj) => {
        return { label: obj["variable_name"], type: "variable", boost: 70, snippet: " #{1:}" }
      })
    return variables
  }


  getVariablesWithDot(context, previous_sibling, fetchall, platform, platform_plugins, editormode) {
    let variablesforjoin = Object.values(this.letVariableMap(context))
      .filter((variableobj) => variableobj["statement"])
      .map((obj) => {
        return { label: obj["variable_name"], type: "variable", boost: 70, snippet: ".#{1:}" }
      })
    return variablesforjoin
  }

  getVariablesWithoutStatement(context, previous_sibling, fetchall, platform, platform_plugins, editormode) {
    let variables = Object.values(this.letVariableMap(context))
      .filter((variableobj) => !variableobj["statement"])
      .map((obj) => {
        return { label: obj["variable_name"], type: "variable", boost: 70, snippet: " #{1:}" }
      })
    return variables
  }

  getVariableFields(context, previous_sibling, fetchall, platform, platform_plugins, editormode) {
    let variable_name = context.state.sliceDoc(previous_sibling[0].from, previous_sibling[0].to)
    let variable_obj = this.letVariableMap(context)[variable_name]
    if (variable_obj) {
      let fetchnode = variable_obj["statement"]?.getChild('Scripts')?.getChild('FetchCommand')
      if (fetchnode) {
        const calc_by_tokens = [].concat(variable_obj["statement"]?.getChild('Scripts')?.getChild('ByCommand') ?? [], variable_obj["statement"]?.getChild('Scripts')?.getChild('CalculateCommand') ?? []);
        if (fetchnode.lastChild.name == "Fetchall" && calc_by_tokens.length) {
          fetchnode = calc_by_tokens
        } else {
          fetchnode = [fetchnode]
        }
        let variable_fields = this.suggest_fetch_variable_fields(context, fetchnode, fetchall, variable_obj["platform"], editormode)
        return variable_fields
      }
    }
    return []
  }

  getVariableFieldsWithoutspace(context, previous_sibling, fetchall, platform, platform_plugins, editormode) {
    let variable_name = context.state.sliceDoc(previous_sibling[0].from, previous_sibling[0].to)
    let variable_obj = this.letVariableMap(context)[variable_name]
    if (variable_obj) {
      let fetchnode = variable_obj["statement"]?.getChild('Scripts')?.getChild('FetchCommand')
      if (fetchnode) {
        const calc_by_tokens = [].concat(variable_obj["statement"]?.getChild('Scripts')?.getChild('ByCommand') ?? [], variable_obj["statement"]?.getChild('Scripts')?.getChild('CalculateCommand') ?? []);
        if (fetchnode.lastChild.name == "Fetchall" && calc_by_tokens.length) {
          fetchnode = calc_by_tokens
        } else {
          fetchnode = [fetchnode]
        }
        let variable_fields = this.suggest_fetch_variable_fields_withoutspace(context, fetchnode, fetchall, variable_obj["platform"], editormode)
        return variable_fields
      }
    }
    return []
  }

  setPlatformWithAlias(context, previous_sibling, fetchall, platform, platform_plugins, editormode) {
    let platformalias = platform_plugins["host"]["external_tokens"]["Platform"].map((pf) => {
      return { label: pf, type: "variable", boost: 70, snippet: `[${pf}] as #{1:S1}`, apply: "" }
    })
    return platformalias
  }

  setBetween(context, previous_sibling, fetchall, platform, platform_plugins, editormode) {
    let current_user_time = this.timeZoneService.getcurrentuserTime()
    let start_time = current_user_time.clone().startOf('day')
    let between = platform_plugins["host"]["external_tokens"]["Between"].map((btw) => {
      return { label: btw, type: "keyword", boost: 76, snippet: `(#{1:${start_time.format('DD-MM-YYYY HH:mm:ss')},${current_user_time.format('DD-MM-YYYY HH:mm:ss')}})` }
    })
    return between
  }

  setBetweenRange(context, previous_sibling, fetchall, platform, platform_plugins, editormode) {
    let current_user_time = this.timeZoneService.getcurrentuserTime()
    let start_time = current_user_time.clone().startOf('day')
    let betweenRange = platform_plugins["host"]["external_tokens"]["BetweenRange"].map((btw) => {
      return { label: btw, type: "keyword", boost: 76, snippet: `#{1:${start_time.format('DD-MM-YYYY HH:mm:ss')},${current_user_time.format('DD-MM-YYYY HH:mm:ss')}}`, apply: "" }
    })
    return betweenRange
  }

  setUnitsForTime(context, previous_sibling, fetchall, platform, platform_plugins, editormode) {
    let units = platform_plugins["host"]["external_tokens"]["Units"].map((unit) => {
      return { label: unit, type: "numeric_value", boost: 76, snippet: `#{1:1}`, apply: "" }
    })
    return units
  }


  map_suggest_function = {
    "SortBy": this.suggest_after_sort.bind(this),
    "Alias": this.suggest_for_alias.bind(this),
    "GetByFields": this.suggest_group_by_fields.bind(this),
    "GetAlias": this.suggest_alias_names.bind(this),
    // "VariableName": this.suggest_variable_name.bind(this),
    "TableAlias": this.getTableAliasName.bind(this),
    "Variable": this.getVariables.bind(this),
    "VariableWithDot": this.getVariablesWithDot.bind(this),
    "VariableFields": this.getVariableFields.bind(this),
    "VariableFieldsWithoutspace": this.getVariableFieldsWithoutspace.bind(this),
    "PlatformWithAlias": this.setPlatformWithAlias.bind(this),
    "VariableWithoutStatement": this.getVariablesWithoutStatement.bind(this),
    "Between": this.setBetween.bind(this),
    "BetweenRange": this.setBetweenRange.bind(this),
    "Units": this.setUnitsForTime.bind(this)
  }

  getStatsCommandNodes(node, pos, context) {
    let statsCommands = ["CalculateCommand", "ByCommand"];
    let statsCommandNode = [];
    let hostscriptsnode = syntaxTree(context.state).cursor().node
    let letcmds = hostscriptsnode?.getChildren('LetCommand')
    if (letcmds.length) {
      letcmds.forEach((letchild) => {
        if (letchild.from <= pos && !(letchild.from > pos) && letchild.to >= pos) {
          statsCommands.forEach(cmd => {
            let cmdnode = letchild.getChild("Statement")?.getChild('Scripts')?.getChild(cmd)
            if (cmdnode) {
              statsCommandNode.push(cmdnode)
            }
          })
        }
      })
    }
    statsCommands.forEach(cmd => {
      let cmdnode = hostscriptsnode.getChild("Statement")?.getChild('Scripts')?.getChild(cmd)
      if (cmdnode) {
        statsCommandNode.push(cmdnode)
      }
    })

    // console.log("<&&&&&&&&&&&&getStatsCommandNodes&&&&&&&&&&&&&",statsCommandNode)
    return statsCommandNode
  }

  getStatsCommandNodesAlert(context) {
    let statsCommands = ["CalculateCommand", "ByCommand"];
    let statsCommandNode = [];

    statsCommands.forEach(cmd => {
      let cmdnode = syntaxTree(context.state).cursor().node.getChild(cmd)
      if (cmdnode) {
        statsCommandNode.push(cmdnode)
      }
    })

    return statsCommandNode
  }

  getGeneralCommandNodes(node, pos, context) {
    //consoe.log("======node=======",node)
    let generalCommandNode = [];
    if (["ImportCommand", "VariableAssignment"].includes(node.name)) {
      generalCommandNode.push(node)
    }
    else if (node.name == "Dot" && node.prevSibling?.name == "Variable" && (["JoinCommand", "FetchCommand", "FilterCommand", "ArithmaticExpression"].includes(node.parent?.name))) {
      generalCommandNode.push(node.prevSibling)
    } else if (node.name == "⚠" && node.prevSibling?.name == "Dot" && node.prevSibling?.prevSibling?.name == "Variable" && (["JoinCommand", "FetchCommand", "FilterCommand"].includes(node.parent?.name))) {
      generalCommandNode.push(node.prevSibling.prevSibling)
    }
    //consoe.log("====================generalCommandNode=================",generalCommandNode)
    return generalCommandNode
  }

  isFetchNodePresent(node, pos, context) {
    let hostscriptsnode = syntaxTree(context.state).cursor().node
    let letcmds = hostscriptsnode?.getChildren('LetCommand')
    let isfetchnode = false;
    if (letcmds.length) {
      letcmds.forEach((letchild) => {
        if (letchild.from <= pos && !(letchild.from > pos) && letchild.to >= pos) {
          if ((letchild.getChild("Statement")?.getChild('Scripts')?.getChild('WhereCommand') && letchild.getChild("Statement")?.getChild('Scripts')?.getChild('WhereCommand')?.firstChild.from <= pos) || (letchild.getChild("Statement")?.getChild('Scripts')?.getChild("FetchCommand") && letchild.getChild("Statement")?.getChild('Scripts')?.getChild("FetchCommand").firstChild.from <= pos)) {
            isfetchnode = true
          } else {
            isfetchnode = false
          }
        }
      })
      return isfetchnode
    } else {
      if ((hostscriptsnode?.getChild("Statement")?.getChild('Scripts')?.getChild('WhereCommand') && hostscriptsnode?.getChild("Statement")?.getChild('Scripts')?.getChild('WhereCommand')?.firstChild.from <= pos) || (hostscriptsnode?.getChild("Statement")?.getChild('Scripts')?.getChild("FetchCommand") && hostscriptsnode?.getChild("Statement")?.getChild('Scripts')?.getChild("FetchCommand").firstChild.from <= pos)) {
        return true
      } else {
        return false
      }
    }
  }

  isFetchNodePresentAlert(context, pos) {
    if ((syntaxTree(context.state).cursor().node.getChild("WhereCommand") && syntaxTree(context.state).cursor().node.getChild("WhereCommand").firstChild.from <= pos) || (syntaxTree(context.state).cursor().node.getChild("FetchCommand") && syntaxTree(context.state).cursor().node.getChild("FetchCommand").firstChild.from <= pos)) {
      return true
    } else {
      return false
    }
  }

  isFetchall(context) {
    let fetchcmd = syntaxTree(context.state).cursor().node.getChild("FetchCommand");
    if (fetchcmd) {
      if (fetchcmd.getChildren("Fetchall").length) {
        return true
      }
      else {
        return false
      }
    } else {
      return true
    }
  }

  getPlatformFromStatement(context, cursor, pos) {
    let foundplatform = ["host"];
    if (cursor.from < pos) {
      let fromttree = cursor.node.getChild('FromCommand');

      let fromcursor = fromttree.cursor();

      if (fromcursor.firstChild()) {
        do {
          if (fromcursor.name == "Alias") {
            let fromalias = context.state.sliceDoc(fromcursor.from, fromcursor.to)
            let platformmap = this.findPlatformAliasFromImport(context)
            foundplatform = platformmap.hasOwnProperty(fromalias) ? platformmap[fromalias] : []
          }
        }
        while (fromcursor.nextSibling())
      }
      return foundplatform
    } else {
      return foundplatform
    }
  }

  findCurrentPlatform(context, pos) {
    let tree = syntaxTree(context.state);
    let cursor = tree.cursor();
    let foundplatform = ["host"];
    if (cursor.firstChild()) {
      do {
        if (cursor.type.name == "Statement") {
          return this.getPlatformFromStatement(context, cursor, pos)
        }
        else if (cursor.type.name == "LetCommand") {
          let statementtree = cursor.node.getChild('Statement');
          if (statementtree) {
            let stcursor = statementtree.cursor();
            if (stcursor.type.name == "Statement") {
              if (cursor.to >= pos) {
                return this.getPlatformFromStatement(context, stcursor, pos)
              }

            }
          }

        }
      } while (cursor.nextSibling());
    }
    return foundplatform
  }

  findPlatformAliasFromImport(context) {
    let platform_alias_map = {};
    let importCommandList = syntaxTree(context.state).cursor().node.getChildren("ImportCommand");
    importCommandList.forEach((command) => {
      if (command.lastChild.name == "Alias") {
        let alias_name = context.state.sliceDoc(command.lastChild.from, command.lastChild.to);
        if (command.firstChild.nextSibling.name == "Tables") {
          let tables = command.firstChild.nextSibling.getChildren("Table")
          let part_dispnames = tables.map(table => context.state.sliceDoc(table.from, table.to))
          let part_valuenames = part_dispnames.map((part: any) => this.sharedService.partitionsMapObj[part])
          platform_alias_map[alias_name] = part_valuenames
        } else if (command.firstChild.nextSibling.name == "Table") {
          let part_disp_name = context.state.sliceDoc(command.firstChild.nextSibling.from, command.firstChild.nextSibling.to)
          let part_valuename = this.sharedService.partitionsMapObj[part_disp_name]
          platform_alias_map[alias_name] = [part_valuename]
        }
      }
    })

    return platform_alias_map
  }

  letVariableMap(context) {
    let variableMapObj = {};
    let LetCommandChilds = syntaxTree(context.state).cursor().node.getChildren("LetCommand")
    LetCommandChilds.forEach((letchild) => {
      let variableAssignement = letchild.getChild('VariableAssignment')
      let variable_name = context.state.sliceDoc(variableAssignement.firstChild.nextSibling.from, variableAssignement.firstChild.nextSibling.to)
      let platform_node = letchild.getChild('Statement')?.getChild('FromCommand')?.firstChild.nextSibling
      let platform_name = "";
      if (platform_node) {
        if (platform_node.name == "Alias") {
          platform_name = this.findPlatformAliasFromImport(context)[context.state.sliceDoc(platform_node.from, platform_node.to)]
        }

      }
      variableMapObj[variable_name] = {
        "variable_name": variable_name,
        "statement": letchild.getChild('Statement'),
        "letvalue": letchild.getChild('VariableValue'),
        "platform": platform_name
      }
    })
    // console.log("=====variableMapObj===================",variableMapObj)
    return variableMapObj
  }

  getExternalTokens(current_platform, platfrom_plugins) {
    let uniqueExternalTokens = {};
    current_platform.forEach(pf => {
      Object.entries(platfrom_plugins[pf]['external_tokens']).forEach(([key, val]) => {
        if (!uniqueExternalTokens[key]) uniqueExternalTokens[key] = new Set();
        if (Array.isArray(val)) {
          val.forEach(v => uniqueExternalTokens[key].add(v));
        } else {
          uniqueExternalTokens[key].add(val);
        }
      })
    });
    Object.keys(uniqueExternalTokens).forEach(k => uniqueExternalTokens[k] = [...uniqueExternalTokens[k]]);
    return uniqueExternalTokens
  }

  findIsRHS(facts, previous_before) {
    if (((facts["current"] == "FilterCommand" || facts["current"] == "JoinCommand") || (facts["parent"] == "FilterCommand" || facts["parent"] == "JoinCommand")) && (this.sharedService.tokens_map_obj["token_category"]["Fields"].includes(facts["previous"]) || facts["previous"] == "Alias") && facts["previous_before"] == "Dot") {
      if (this.sharedService.tokens_map_obj["token_category"]["ComparisonOperators"].includes(previous_before?.prevSibling?.prevSibling?.name)) {
        return true
      }
    } else if ((facts["current"] == "FilterCommand" || facts["parent"] == "FilterCommand") && (["StringValue", "NumericValue", "IpAddress", "Alias", "Variable"].concat(this.sharedService.tokens_map_obj["token_category"]["WildCard"]).includes(facts["previous"]))) {
      return true
    } else if (facts["parent"] == "FilterCommand" && this.sharedService.tokens_map_obj["token_category"]["WildCard"].includes(facts["previous_sibling"])) {
      return true
    } else{
      return null
    }
    return false
  }

  isJoinFetch(node) {
    if (node.name == "FetchCommand") {
      if (node.prevSibling?.name == "FilterCommand" || node.prevSibling?.name == "JoinCommand") {
        return true
      } else {
        return false
      }
    } else if (node.parent?.name == "FetchCommand") {
      if (node.parent.prevSibling?.name == "FilterCommand" || node.parent.prevSibling?.name == "JoinCommand") {
        return true
      } else {
        return false
      }
    } else {
      return false
    }
  }

  async customCompletion(context, platfrom_plugins, host = "nothost", editormode) {
    const tree = syntaxTree(context.state)
    const node = tree.resolve(context.pos, -1)

    if (this.enable_auto_suggestion) {
      this.suggestion = JSON.parse(sessionStorage.getItem("query_fields"))
      const pos = context.pos;
      const { previous, previous_before, current, next, next_after, parent, previous_sibling } = this.getContextNodes(context.state, pos, editormode);

      let word = { "from": pos, "to": pos, "text": context.state.sliceDoc(pos, pos) };

      if (current?.parent && !current?.firstChild && !["openparam", "Comma", "Dot", "Semicolon"].includes(current?.name)) {

        word = { "from": current.from, "to": current.to, "text": context.state.sliceDoc(current.from, current.to) };
      }

      let facts = {
        explicit: context.explicit,
        error: this.isEditorError(context),
        previous: previous?.name || null,
        previous_before: previous_before?.name || null,
        parent: parent?.name || null,
        next: next?.name || null,
        next_after: next_after?.name || null,
        current: current?.name || null,
        previous_sibling: previous_sibling?.name || null
      }
      let statsCommandNode = this.getStatsCommandNodes(node, pos, context)
      let generalCommandNode = this.getGeneralCommandNodes(node, pos, context)
      let is_fetchnode_present = this.isFetchNodePresent(node, pos, context)
      let is_fetchall_present = this.isFetchall(context)
      facts["statsCommand"] = Boolean(statsCommandNode.length) && is_fetchnode_present
      facts["is_fetchall"] = is_fetchall_present
      facts["isJoinFetch"] = this.isJoinFetch(node)
      facts["isRHS"] = this.findIsRHS(facts, previous_before)
      let current_platform = this.findCurrentPlatform(context, pos)
      // console.log("$$$$$$$$$$$current platform$$$$$$$$$$$$$",current_platform)
      //consoe.log("<=======##==facts====##=>",facts)

      let result: any = {
        from: word.from,
        options: [],
        validFor: /[a-zA-Z0-9=!<> ]*/
      }
      if (current_platform && current_platform.length) {
        let pf_external_tokens = this.getExternalTokens(current_platform, platfrom_plugins)
        let { events } = await this.search_rule_engine.run(facts)
        let suggestion = []
        events.map(element => {
          //consoe.log("element=======",element)
          element.params.next.forEach(token => {
            if (token.token in this.map_suggest_function) {
              suggestion = suggestion.concat(this.map_suggest_function[token.token](context, [...statsCommandNode, ...generalCommandNode], is_fetchall_present, current_platform, platfrom_plugins, editormode)
                .map(s => snippetCompletion(s.hasOwnProperty('apply') ? `${s.snippet}` : `${s.label}${s.snippet}`, { label: s.label, type: s.type, boost: s.hasOwnProperty('boost') ? s.boost : 99 })))
            } else if (token.hasOwnProperty('apply')) {
              suggestion = suggestion.concat(pf_external_tokens[token.token].map(s => snippetCompletion(`${token.snippet}`, { label: s, type: token.type, boost: token.hasOwnProperty('boost') ? token.boost : 99 })))
            } else {
              suggestion = suggestion.concat(pf_external_tokens[token.token].map(s => snippetCompletion(`${s}${token.snippet}`, { label: s, type: token.type, boost: token.hasOwnProperty('boost') ? token.boost : 99 })))
            }
          });
          result = {
            from: word.from,
            options: suggestion,
            validFor: /[a-zA-Z0-9=!<> ]*/
          }

        });
      }

      return result
    }
  }

  async customCompletionAlert(context, platfrom_plugins, host = "nothost", editormode) {
    // console.log("=========platfrom_plugins======",platfrom_plugins)
    const tree = syntaxTree(context.state)
    const node = tree.resolve(context.pos, -1)

    if (this.enable_auto_suggestion) {
      const pos = context.pos;
      const { previous, previous_before, current, next, next_after, parent, previous_sibling } = this.getContextNodes(context.state, pos, editormode);

      let word = { "from": pos, "to": pos, "text": context.state.sliceDoc(pos, pos) };

      if (current?.parent && !current?.firstChild && !["openparam", "Comma", "Dot", "Semicolon"].includes(current?.name)) {

        word = { "from": current.from, "to": current.to, "text": context.state.sliceDoc(current.from, current.to) };
      }

      let facts = {
        explicit: context.explicit,
        error: this.isEditorError(context),
        previous: previous?.name || null,
        previous_before: previous_before?.name || null,
        parent: parent?.name || null,
        next: next?.name || null,
        next_after: next_after?.name || null,
        current: current?.name || null,
        previous_sibling: previous_sibling?.name || null
      }
      let statsCommandNode = this.getStatsCommandNodesAlert(context)
      let is_fetchnode_present = this.isFetchNodePresentAlert(context, pos)
      let is_fetchall_present = this.isFetchall(context)
      facts["statsCommand"] = Boolean(statsCommandNode.length) && is_fetchnode_present
      facts["is_fetchall"] = is_fetchall_present
      //consoe.log("<=======##==facts====##=>",facts)

      let result: any = {
        from: word.from,
        options: [],
        validFor: /[a-zA-Z0-9=!<> ]*/
      }
      let pf_external_tokens = this.getExternalTokens(Object.keys(platfrom_plugins), platfrom_plugins)
      let { events } = await this.alert_rule_engine.run(facts)
      let suggestion = []
      events.map(element => {
        //consoe.log("element=======",element)
        element.params.next.forEach(token => {
          if (token.token in this.map_suggest_function) {
            suggestion = suggestion.concat(this.map_suggest_function[token.token](context, [...statsCommandNode, ...[]], is_fetchall_present, Object.keys(platfrom_plugins), platfrom_plugins, editormode)
              .map(s => snippetCompletion(s.hasOwnProperty('apply') ? `${s.snippet}` : `${s.label}${s.snippet}`, { label: s.label, type: s.type, boost: s.hasOwnProperty('boost') ? s.boost : 99 })))
          } else if (token.hasOwnProperty('apply')) {
            suggestion = suggestion.concat(pf_external_tokens[token.token].map(s => snippetCompletion(`${token.snippet}`, { label: s, type: token.type, boost: token.hasOwnProperty('boost') ? token.boost : 99 })))
          } else {
            suggestion = suggestion.concat(pf_external_tokens[token.token].map(s => snippetCompletion(`${s}${token.snippet}`, { label: s, type: token.type, boost: token.hasOwnProperty('boost') ? token.boost : 99 })))
          }
        });
        result = {
          from: word.from,
          options: suggestion,
          validFor: /[a-zA-Z0-9=!<> ]*/
        }

      });

      return result
    }
  }

  uppercaseFilter(value, index, array) {
    return value == value.toUpperCase();
  }

  field_type_map = {
    StringFields: "string_fields",
    NumericFields: "numeric_fields",
    IpFields: "string_fields",
    TimestampFields: "timestamp_fields"
  }

  get_AliasName(editor, cursor, default_str) {
    let alias_name = "";
    if (cursor.node.nextSibling?.name == "As") {
      cursor.nextSibling()
      cursor.nextSibling()
      alias_name = editor.state.sliceDoc(cursor.from, cursor.to).replace(/!^"|!$"/g, "'");
    } else {
      alias_name = default_str.replace(/^"|"$/g, '')
      alias_name = alias_name.replace(/"/g, "'")
      alias_name = `"${alias_name}"`

    }
    return alias_name;
  }

  // for fetch all
  buildAllFields(field_names, platforms, editormode) {
    let tmp_field_names = []
    if (editormode == 'rule') {
      platforms.forEach(pf => {
        this.sharedService.platform_plugins[pf]["platform_fields"].forEach(field_obj => {
          if (field_obj.is_entity) {
            if (!tmp_field_names.includes(field_obj.value)) {
              field_names.push({ "name": field_obj.value, "type": field_obj.type, "pos": 0 });
              tmp_field_names.push(field_obj.value)
            }
          }
        })
      });

    } else if (editormode == 'search') {
      platforms.forEach(pf => {
        this.sharedService.platform_plugins[pf]["platform_fields"].forEach(field_obj => {
          if (!tmp_field_names.includes(field_obj.value)) {
            field_names.push({ "name": field_obj.value, "type": field_obj.type, "pos": 0 });
            tmp_field_names.push(field_obj.value)
          }
        })
      })
    } else {
      platforms.forEach(pf => {
        this.sharedService.tokens_map_obj['token_category']["Fields"].forEach((field_type => {
          let token_cat_obj = this.sharedService.tokens_map_obj['tokens_type_map'][field_type]
          this.sharedService.platform_plugins[pf]["external_tokens"][field_type].forEach(field => {
            if (!tmp_field_names.includes(field)) {
              field_names.push({ "name": field, "type": token_cat_obj.type, "pos": 0 })
              tmp_field_names.push(field)
            }
          })
        }))
      })

    }
    tmp_field_names.length = 0
    return field_names
  }

  //get all alias names for given command
  getAliasName(command, context, platform, editormode) {
    let alias_names = [];
    let field_names = [];
    // console.log("=====command name====",command)
    command.forEach(command_child => {
      let command_cursor = command_child.node.firstChild.cursor();
      do {
        // console.log("=============command_cursor.name===============", command_cursor.name)
        if (this.sharedService.tokens_map_obj['token_category']["Functions"].includes(command_cursor.name) || this.sharedService.tokens_map_obj['token_category']["Expression"].includes(command_cursor.name)) {
          // let default_str = context.state.sliceDoc(command_cursor.from, command_cursor.to).replace(/^"|"$/g, '');
          let default_str = context.state.sliceDoc(command_cursor.from, command_cursor.to);
          let alias_name = this.get_AliasName(context, command_cursor, default_str)
          alias_names.push({ "name": alias_name, "type": "alias_name", "pos": command_cursor.from });
        } else if (this.sharedService.tokens_map_obj['token_category']["Fields"].includes(command_cursor.name)) {
          let field_name = context.state.sliceDoc(command_cursor.from, command_cursor.to)
          field_names.push({ "name": field_name, "type": this.field_type_map[command_cursor.name] ? this.field_type_map[command_cursor.name] : "string_fields", "pos": command_cursor.from });
        } else if (command_cursor.name == "Alias") {
          let alias_name = context.state.sliceDoc(command_cursor.from, command_cursor.to).replace(/!^"|!$"/g, "'");
          alias_names.push({ "name": alias_name, "type": "alias_name", "pos": command_cursor.from });
        }
        else if (command_cursor.name == "Fetchall") {
          field_names = this.buildAllFields(field_names, platform, editormode)
          // console.log("<==========field_names============>",field_names)
        }
        else if (command_cursor.name == "Table") {

          let table_name = context.state.sliceDoc(command_cursor.from, command_cursor.to)
          let importchild = syntaxTree(context.state).cursor().node.getChildren("ImportCommand")
          let source_name = "S1";
          importchild.forEach((impchild, impind) => {
            if (impchild.name == "Table") {
              if (context.state.sliceDoc(impchild.from, impchild.to) == table_name) {
                source_name = "S" + impind + 1
              }
            }
          })
          alias_names.push({ "name": source_name, "type": "alias_name", "pos": command_cursor.from });
        }
      } while (command_cursor.nextSibling())
    })

    // console.log("alias_names:",alias_names)
    // console.log("field_names:",field_names)

    return { alias_names, field_names }
  }

  find_alias_name(command, context, platform, editormode) {
    let suggest_names = {};
    suggest_names = { ...this.getAliasName(command, context, platform, editormode) }
    return suggest_names
  }

  find_current_alias_name(command, context, platform, editormode) {
    const cursor_pos = context.state.selection.main.head;
    let suggest_names = this.find_alias_name(command, context, platform, editormode)
    let last_alias_name = suggest_names["alias_names"][suggest_names["alias_names"].findLastIndex((x, i, sugg) => {
      const next = sugg[i + 1];
      if ((next?.pos >= cursor_pos && x.pos <= cursor_pos) || (x.pos <= cursor_pos)) return x
    })];
    return last_alias_name

  }

  focusextension(callback) {
    return EditorView.focusChangeEffect.of((_, focusing) => {
      callback(focusing);
      return null
    });
  }

  get_editor_value(editor) {
    return editor.state.doc.toString()
  }

  set_editor_value(editor, value) {
    editor.dispatch({
      changes: { from: 0, to: editor.state.doc.length, insert: value }
    })
  }

  append_editor_value(editor, value) {
    editor.dispatch({
      changes: { from: editor.state.doc.length, insert: value }
    })
  }

  isEditorhasLint(editor: EditorView) {
    let lints = []
    forEachDiagnostic(editor.state, (error) => {
      lints.push(error)
    })
    return lints
  }

  isEditorError(editor: EditorView) {
    let isError: boolean = false;
    syntaxTree(editor.state).cursor().iterate((node) => {
      if (node.type.isError || diagnosticCount(editor.state)) {
        isError = true;
        return
      }
    })
    return isError;
  }

  csiem_query(platfrom_plugins, partitions?, mode?) {
    const language = this.csiem_query_Language(platfrom_plugins, partitions, mode)
    return new LanguageSupport(language)
  }

  initEditor(htmlElement: HTMLElement, suggestion, readonly: boolean, focusCallback: Function, grammar: string, mode = 'search') {
    this.suggestion = suggestion
    this.mode = mode

    // console.log(this.customLang.parser)
    return new EditorView({
      doc: this.editorValue,
      extensions: [
        basicSetup,
        keymap.of([indentWithTab]),
        // this.customLanguage,

        this.csiem_query(grammar, "", mode),
        this.myTheme,
        // this.mode == 'rule' ? this.myLinter_rule : this.myLinter,
        this.focusextension(focusCallback),
        EditorState.readOnly.of(readonly),
        // autocompletion({ override: [this.customCompletion] }),
        // autocompletion({ override: [(context: CompletionContext) => this.customCompletion(context)] }),
        autocompletion({ override: [this.customCompletion.bind(this)], activateOnCompletion: () => { return true } }),

        syntaxHighlighting(this.myHighlightStyle)
      ],
      parent: htmlElement,
    })
  }
  initializeEditor(htmlElement: HTMLElement, query: string, readonly: boolean, grammar: any, mode = 'rule', partitions?, partitionsFromEditor?, editorValueSubject?) {

    this.mode = mode
    let language = new Compartment
    let autocompleteCompartment = new Compartment()
    let lintCompartment = new Compartment()
    return new EditorView({
      doc: query,
      extensions: [
        keymap.of([indentWithTab]),
        basicSetup,
        // this.csiem_query(grammar, partitions, mode),
        language.of(this.csiem_query(grammar, partitions, mode)),
        autocompleteCompartment.of(this.buildAutocomplete(grammar, mode)),
        lintCompartment.of(this.buildLinter(mode)),
        this.myTheme,
        // this.mode == 'rule' ? this.myLinter_rule : this.myLinter,
        EditorState.readOnly.of(readonly),
        // autocompletion({ override: [this.customCompletion.bind(this)], activateOnCompletion: () => { return true } }),
        syntaxHighlighting(this.myHighlightStyle),
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            // previous text in the editor, before the change
            // current text in the editor
            const prevValue = update.startState.doc.toString();
            const newValue = update.state.doc.toString();
            let PrevPartitionsText = this.getTablesFromQuery(prevValue)
            let partitionsText = this.getTablesFromQuery(newValue)
            // reconfigure language compartment if the IMPORT statement is modified
            if (partitionsText.length && !this.areArraysEqual(PrevPartitionsText, partitionsText)) {
              //async to avoid transaction conflicts
              setTimeout(() => {
                let partitionsObj = []
                this.sharedService.partitions["rows"].forEach(element => {
                  if (partitionsText.includes(element.partition_name_disp)) {
                    partitionsObj.push(element)
                  }
                });
                this.sharedService.getLanguageGrammar(partitionsText, partitionsObj)
                  .then((platform_plugins: any) => {

                    let tmp_platform_plugins = { ...platform_plugins, ...JSON.parse(JSON.stringify(this.sharedService.host_plugins)) }
                    let pfids = [...new Set(partitionsObj.map(ptobj => ptobj.platform_id.toString()))]
                    tmp_platform_plugins["host"]["external_tokens"]["Platform"] = this.sharedService.partitions["rows"].map((element) => {

                      return element["partition_name_disp"];
                    });

                    Object.values(platform_plugins).forEach((pluginobj) => {
                      tmp_platform_plugins["host"]["external_tokens"]["StringFields"].concat(pluginobj["external_tokens"]["StringFields"])
                      tmp_platform_plugins["host"]["external_tokens"]["NumericFields"].concat(pluginobj["external_tokens"]["NumericFields"])
                      tmp_platform_plugins["host"]["external_tokens"]["IpFields"].concat(pluginobj["external_tokens"]["IpFields"])
                      tmp_platform_plugins["host"]["external_tokens"]["TimestampFields"].concat(pluginobj["external_tokens"]["TimestampFields"])
                      tmp_platform_plugins["host"]["external_tokens"]["StringListValue"].concat(pluginobj["external_tokens"]["StringListValue"])
                      tmp_platform_plugins["host"]["external_tokens"]["NumericListValue"].concat(pluginobj["external_tokens"]["NumericListValue"])
                      tmp_platform_plugins["host"]["external_tokens"]["IPListValue"].concat(pluginobj["external_tokens"]["IPListValue"])
                    })
                    const newLang = this.csiem_query(tmp_platform_plugins, partitionsObj, mode)
                    const newAutocomplete = this.buildAutocomplete(tmp_platform_plugins, mode)
                    const newLinter = this.buildLinter(mode)
                    update.view.dispatch({
                      effects: [language.reconfigure(newLang), autocompleteCompartment.reconfigure(newAutocomplete), lintCompartment.reconfigure(newLinter)]
                    })
                    partitionsFromEditor ? partitionsFromEditor.next(partitionsObj) : null
                  })
              })
            }
            // passing value changes
            editorValueSubject ? editorValueSubject.next(newValue) : this.editorValueSubject.next(newValue);
          }
        }),
      ],

      parent: htmlElement,
    })
  }
  // initNotebookEditor(htmlElement: HTMLElement, readonly: boolean, focusCallback: Function, platfrom_plugins: any, mode = 'search', editorCompartment,partitions, editorValueSubject?
  // ) {
  //   // this.suggestion = suggestion
  //   this.mode = mode
  //   return new EditorView({
  //     doc: this.editorValue,
  //     extensions: [
  //       basicSetup,
  //       keymap.of([indentWithTab]),
  //       // this.customLanguage,

  //       this.csiem_query(platfrom_plugins,partitions,mode),

  //       this.myTheme,
  //       this.focusextension(focusCallback),
  //       editorCompartment.of(EditorState.readOnly.of(readonly)),
  //       EditorView.updateListener.of((update) => {
  //         if (update.docChanged) {
  //           const newValue = update.state.doc.toString();

  //           editorValueSubject ? editorValueSubject.next(newValue) : this.editorValueSubject.next(newValue);
  //         }
  //       }),
  //     ].concat(this.qryLang2(platfrom_plugins,mode)),
  //     parent: htmlElement,
  //   })
  // }


  areArraysEqual(arr1: any[], arr2: any[]): boolean {
    if (arr1.length !== arr2.length) {
      return false;
    }
    return arr1.every((element, index) => element === arr2[index]);
  }
  buildAutocomplete(platfrom_plugins, mode) {
    return autocompletion({
      activateOnCompletion: () => { return true },
      activateOnTyping: true,
      override: [
        (context) => this.customCompletion(context, platfrom_plugins, "host", mode)
      ]
    })
  }
  buildLinter(mode) {
    return linter((view) => this.csiem_host_lint(view, mode))
  }
  initNotebookEditor(htmlElement: HTMLElement, readonly: boolean, focusCallback: Function, platfrom_plugins: any, mode = 'search', editorCompartment, partitions, editorValueSubject?, partitionsFromEditor?
  ) {
    // this.suggestion = suggestion
    this.mode = mode
    let language = new Compartment
    let autocompleteCompartment = new Compartment()
    let lintCompartment = new Compartment()
    return new EditorView({
      doc: this.editorValue,
      extensions: [
        basicSetup,
        keymap.of([indentWithTab]),
        // linter(view => { return this.csiem_host_lint(view, mode) }),

        language.of(this.csiem_query(platfrom_plugins, partitions, mode)),
        autocompleteCompartment.of(this.buildAutocomplete(platfrom_plugins, mode)),
        lintCompartment.of(this.buildLinter(mode)),
        this.myTheme,
        this.focusextension(focusCallback),
        editorCompartment.of(EditorState.readOnly.of(readonly)),
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            // previous text in the editor, before the change
            // current text in the editor
            const prevValue = update.startState.doc.toString();
            const newValue = update.state.doc.toString();
            let PrevPartitionsText = this.getTablesFromQuery(prevValue)
            let partitionsText = this.getTablesFromQuery(newValue)
            // reconfigure language compartment if the IMPORT statement is modified
            if (partitionsText.length && !this.areArraysEqual(PrevPartitionsText, partitionsText)) {
              //async to avoid transaction conflicts
              setTimeout(() => {
                let partitionsObj = []
                this.sharedService.partitions["rows"].forEach(element => {
                  if (partitionsText.includes(element.partition_name_disp)) {
                    partitionsObj.push(element)
                  }
                });
                this.sharedService.getLanguageGrammar(partitionsText, partitionsObj)
                  .then((platform_plugins: any) => {
// console.log(platform_plugins)

                    let tmp_platform_plugins = { ...platform_plugins, ...JSON.parse(JSON.stringify(this.sharedService.host_plugins)) }
                    let pfids = [...new Set(partitionsObj.map(ptobj => ptobj.platform_id.toString()))]
                    tmp_platform_plugins["host"]["external_tokens"]["Platform"] = this.sharedService.partitions["rows"].map((element) => {

                      return element["partition_name_disp"];
                    });

                    Object.values(platform_plugins).forEach((pluginobj) => {
                      tmp_platform_plugins["host"]["external_tokens"]["StringFields"].concat(pluginobj["external_tokens"]["StringFields"])
                      tmp_platform_plugins["host"]["external_tokens"]["NumericFields"].concat(pluginobj["external_tokens"]["NumericFields"])
                      tmp_platform_plugins["host"]["external_tokens"]["IpFields"].concat(pluginobj["external_tokens"]["IpFields"])
                      tmp_platform_plugins["host"]["external_tokens"]["TimestampFields"].concat(pluginobj["external_tokens"]["TimestampFields"])
                      tmp_platform_plugins["host"]["external_tokens"]["StringListValue"].concat(pluginobj["external_tokens"]["StringListValue"])
                      tmp_platform_plugins["host"]["external_tokens"]["NumericListValue"].concat(pluginobj["external_tokens"]["NumericListValue"])
                      tmp_platform_plugins["host"]["external_tokens"]["IPListValue"].concat(pluginobj["external_tokens"]["IPListValue"])
                    })
                    const newLang = this.csiem_query(tmp_platform_plugins, partitionsObj, mode)
                    const newAutocomplete = this.buildAutocomplete(tmp_platform_plugins, mode)
                    const newLinter = this.buildLinter(mode)
                    update.view.dispatch({
                      effects: [language.reconfigure(newLang), autocompleteCompartment.reconfigure(newAutocomplete), lintCompartment.reconfigure(newLinter)]
                    })
                    partitionsFromEditor ? partitionsFromEditor.next(partitionsObj) : null
                  })
              })
            }
            // passing value changes
            editorValueSubject ? editorValueSubject.next(newValue) : this.editorValueSubject.next(newValue);
          }
        }),
      ].concat(this.qryLang2(platfrom_plugins, mode)),
      parent: htmlElement,
    })
  }
  getTablesFromQuery(qry) {
    // let partitions: Set<string> = new Set()
    // qry.split("\n").forEach((line, index) => {
    //   if (line.includes("IMPORT")) {
    //     line.replace("IMPORT", "\nIMPORT").split("\n").forEach(element => {
    //       if (element) {
    //         let matches = element.match("^\s*IMPORT(.*)\s*as");
    //         let sources = matches ? matches[1].replace("[", "").replace("]", "") : ""
    //         sources.split(",").forEach(partition => {
    //           partitions.add(partition.trim())
    //         });
    //       }
    //     });
    //   }
    // });
    // return Array.from(partitions).sort()

    const bracketedRegex = /IMPORT\s*\[([\s\S]*?)\]\s*as\b/gi;
    const unbracketedRegex = /IMPORT\s+([^\[\s][^\n]*?)\s+as\b/gi;

    const parseValues = (raw) =>
      raw.split(',').map(v => v.trim()).filter(v => v.length > 0);

    let match;
    const values = [];

    while ((match = bracketedRegex.exec(qry)) !== null) {
      values.push(...parseValues(match[1]));
    }

    while ((match = unbracketedRegex.exec(qry)) !== null) {
      values.push(...parseValues(match[1]));
    }

    return [...new Set(values)].sort();
  }

  initEditorAlertRule(htmlElement: HTMLElement, readonly: boolean, platfrom_plugins: any, mode = 'alert', editorCompartment, partitions, editorValueSubject?, alertId?
  ) {
    // this.suggestion = suggestion
    this.mode = mode
    this.createSuggestionRuleEngine(mode)
    return new EditorView({
      doc: this.editorValue,
      extensions: [
        basicSetup,
        keymap.of([indentWithTab]),
        // this.customLanguage,

        // this.csiem_query(platfrom_plugins,partitions),
        autocompletion({ activateOnCompletion: () => { return true } }),

        this.qryLangAlertRule(platfrom_plugins, partitions, alertId, mode),

        this.myTheme,
        // this.mode == 'rule' ? this.myLinter_rule : this.myLinter,
        // this.focusextension(focusCallback),
        editorCompartment.of(EditorState.readOnly.of(readonly)),

        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            const newValue = update.state.doc.toString();

            editorValueSubject ? editorValueSubject.next(newValue) : this.editorValueSubject.next(newValue);
          }
        }),
      ],
      parent: htmlElement,
    })
  }
}